// Garment design/customisation tool.
//
// Two garment "kinds", unified behind one render path:
//  - Vector garments (window.GARMENTS_VECTOR): flat technical-flat illustrations.
//    Colour is just an SVG fill — always crisp, at any colour, no image processing.
//  - Photo garments (window.GARMENTS, currently mug/bottle only): a real product
//    photo, recoloured on canvas (see getRecoloredSrc below).
// Both produce an image src (a data: URI either way) through getGarmentPreviewSrc(),
// so the rest of the tool — preview, drag-to-place zone, download, cart export —
// doesn't need to know which kind it's looking at.
//
// The only network call is the final native /cart/add form submission, which
// carries the composited mockup PNG as a Shopify file-type line item property.
(function () {
  var root = document.getElementById('dt-root');
  if (!root) return;

  var VECTOR_GARMENTS = window.GARMENTS_VECTOR || [];
  var PHOTO_GARMENTS = window.GARMENTS || [];
  var GARMENTS = VECTOR_GARMENTS.concat(PHOTO_GARMENTS);
  // T-Shirt is the flagship/default garment — keep it first in the picker
  // grid regardless of which underlying array (vector vs. photo) it lives in.
  GARMENTS.sort(function (a, b) {
    if (a.id === 'tshirt') return -1;
    if (b.id === 'tshirt') return 1;
    return 0;
  });
  var GARMENT_COLORS = window.GARMENT_COLORS || [];
  var GARMENT_PRODUCTS = window.GARMENT_PRODUCTS || {};
  var CATEGORY_META = window.GARMENT_CATEGORY_META || {};

  // Groups GARMENTS for the "Choose Garment" picker: a category with a single
  // garment (e.g. "mug") is picked directly, same as before; a category with
  // more than one (currently just "cap": Trucker + Vintage) shows one tile
  // first, and only reveals the individual garments as a second screen once
  // clicked — see renderGarmentPicker below.
  var GARMENT_CATEGORIES = (function () {
    var order = [];
    var byKey = {};
    GARMENTS.forEach(function (g) {
      var key = g.category || g.id;
      if (!byKey[key]) { byKey[key] = []; order.push(key); }
      byKey[key].push(g);
    });
    // Top-level tile order: categories with an explicit meta.order (set by
    // the "Painel do Editor" Chrome extension's reorder feature) sort by
    // that; everything else keeps the original first-seen order (which is
    // how every category has always been ordered until now — the T-Shirt
    // hoist above is what puts it first in that fallback order), so this
    // is a no-op until the extension actually assigns explicit positions.
    var withIndex = order.map(function (key, idx) { return { key: key, idx: idx }; });
    withIndex.sort(function (a, b) {
      var oa = CATEGORY_META[a.key] && CATEGORY_META[a.key].order;
      var ob = CATEGORY_META[b.key] && CATEGORY_META[b.key].order;
      if (oa == null && ob == null) return a.idx - b.idx;
      if (oa == null) return 1;
      if (ob == null) return -1;
      return oa - ob;
    });
    return withIndex.map(function (entry) {
      var key = entry.key;
      var items = byKey[key].slice().sort(function (a, b) {
        return (a.categoryOrder || 0) - (b.categoryOrder || 0);
      });
      var meta = CATEGORY_META[key] || {};
      return {
        key: key,
        items: items,
        name: meta.name || items[0].name,
        icon: meta.icon || items[0].icon,
      };
    });
  })();

  // ─── Real Shopify products (tagged "Blanks") ───────────────────────────
  // Matched to a garment by product.type === garment.productType. When matched,
  // that product's real variants (colour, price) drive the colour picker and
  // Add to Cart directly — see matchedProduct() / colorsForGarment() below.
  var BLANK_PRODUCTS = window.DT_BLANK_PRODUCTS || [];
  var SHOP_CURRENCY = window.DT_SHOP_CURRENCY || 'EUR';
  // One fee product per technique (Embroidery / Print — see design-tool.liquid),
  // since they can be priced differently. { id, variantId, priceCents } or null
  // if that technique's product hasn't been created/named yet.
  var FEE_PRODUCTS = window.DT_FEE_PRODUCTS || {};
  function feeProductFor(technique) { return FEE_PRODUCTS[technique] || null; }

  // How many views actually have a design placed on them right now — this
  // is what "Posições" pricing counts, computed automatically from what the
  // customer has actually done rather than asked as a separate question.
  function positionsWithDesignCount() {
    var n = 0;
    Object.keys(state.viewLayers).forEach(function (k) { if (state.viewLayers[k].length > 0) n++; });
    return Math.max(1, n);
  }

  // Picks the exact-priced variant for (technique, how many views have a
  // design) once the owner has set one up via the Chrome extension's Preços
  // screen — see design-tool.liquid's DT_FEE_PRODUCTS, where each
  // technique's variants get "Posições"/"Cor" options provisioned
  // (background.js's ensureTechniquePricing). Colour vs Preto e Branco used
  // to change the price too, but the owner decided that distinction
  // shouldn't affect price at all — a provisioned technique may still carry
  // both tiers in Shopify with different prices, so this pins to the
  // "Cores" tier (what customers were already being charged by default)
  // rather than leaving that ambiguous. Falls back gracefully — closest
  // AVAILABLE position count at or below what's needed (never silently
  // charges LESS than a lower, configured tier), then the technique's own
  // plain default variant — so an unconfigured or only-partially-configured
  // technique still charges something sane instead of ever blocking
  // checkout.
  function selectFeeVariant(technique) {
    var p = feeProductFor(technique);
    if (!p) return null;
    if (!p.variants || !p.variants.length) return { id: p.variantId, priceCents: p.priceCents };
    var positions = positionsWithDesignCount();
    var exact = p.variants.filter(function (v) { return v.option1 === String(positions) && v.option2 === 'Cores'; })[0];
    if (exact) return { id: exact.id, priceCents: exact.price };
    var coresVariants = p.variants.filter(function (v) { return v.option2 === 'Cores'; });
    if (coresVariants.length) {
      var atOrBelow = coresVariants.filter(function (v) { return (parseInt(v.option1, 10) || 0) <= positions; });
      atOrBelow.sort(function (a, b) { return (parseInt(b.option1, 10) || 0) - (parseInt(a.option1, 10) || 0); });
      var pick = atOrBelow[0] || coresVariants[0];
      return { id: pick.id, priceCents: pick.price };
    }
    // No Posições/Cor options configured at all yet — plain single-variant technique.
    return { id: p.variantId, priceCents: p.priceCents };
  }

  function customizationFeeCentsFor(technique) {
    var variant = selectFeeVariant(technique);
    return variant ? variant.priceCents : 700; // fallback so pricing still shows something sane before the products exist
  }

  // ─── Print pricing: real cm size, not position count ───────────────────
  // Print no longer prices by "how many positions total" — the owner's own
  // size table, one tier per position, all summed (a design on the Front
  // AND the Back means two separate charges, one per its own size). Sorted
  // ascending by maxCm; a size picks the first tier it fits within, so
  // anything between two published sizes (e.g. 33cm, between the 30cm and
  // 40cm tiers) rounds up to the next one — same "size bracket" convention
  // most print shops use. optionValue must match the "Tamanho" option
  // values provisioned on the real Print product (see the Preços screen /
  // background.js).
  var PRINT_SIZE_TIERS = [
    { maxCm: 10, optionValue: '10x10cm', priceCents: 500 },
    { maxCm: 15, optionValue: '15x15cm', priceCents: 700 },
    { maxCm: 20, optionValue: '20x20cm', priceCents: 1000 },
    { maxCm: 25, optionValue: '25x25cm', priceCents: 1200 },
    { maxCm: 30, optionValue: '30x30cm', priceCents: 1500 },
    { maxCm: Infinity, optionValue: '40x40cm e acima', priceCents: 1700 },
  ];
  function printTierForCm(sizeCm) {
    for (var i = 0; i < PRINT_SIZE_TIERS.length; i++) {
      if (sizeCm <= PRINT_SIZE_TIERS[i].maxCm) return PRINT_SIZE_TIERS[i];
    }
    return PRINT_SIZE_TIERS[PRINT_SIZE_TIERS.length - 1];
  }
  // Picks the real Shopify variant for a given tier — same graceful
  // fallback shape as selectFeeVariant: if the Print product hasn't been
  // provisioned with "Tamanho" variants yet, falls back to its plain
  // default variant rather than ever blocking checkout.
  function selectPrintFeeVariant(tier) {
    var p = feeProductFor('Print');
    if (!p) return null;
    if (p.variants && p.variants.length) {
      var match = p.variants.filter(function (v) { return v.option1 === tier.optionValue; })[0];
      if (match) return { id: match.id, priceCents: match.price };
    }
    return { id: p.variantId, priceCents: p.priceCents };
  }

  // Real-world cm size of a placed layer within its zone — mirrors the
  // exact same "60% of zone, fit to the image's own aspect ratio, then
  // apply layer.scale" formula used to size the on-screen layer box
  // (sizeLayerElement) and the composited mockup (compositeGarmentView),
  // just in centimetres instead of screen/canvas pixels. Needs the
  // garment's realWidthCm calibration (see garments-data.js) AND the
  // photo's own actual pixel dimensions (imgW/imgH) — those give the
  // photo's real aspect ratio, needed to convert the zone's height
  // percentage into real cm too, not just its width. Returns null if this
  // garment has no calibration yet (a brand-new item the owner just added,
  // for instance) rather than guessing.
  function layerSizeCm(layer, zone, garment, imgW, imgH) {
    if (!garment.realWidthCm || !imgW || !imgH) return null;
    var realHeightCmTotal = garment.realWidthCm * (imgH / imgW);
    var zoneWCm = garment.realWidthCm * (zone.wPct / 100);
    var zoneHCm = realHeightCmTotal * (zone.hPct / 100);
    var boxWCm = zoneWCm * 0.6, boxHCm = zoneHCm * 0.6;
    var boxAspect = boxWCm / boxHCm;
    var aspect = layer._aspect || 1;
    var baseWCm, baseHCm;
    if (aspect > boxAspect) { baseWCm = boxWCm; baseHCm = boxWCm / aspect; }
    else { baseHCm = boxHCm; baseWCm = boxHCm * aspect; }
    var wCm = baseWCm * layer.scale, hCm = baseHCm * layer.scale;
    return { widthCm: wCm, heightCm: hCm, maxCm: Math.max(wCm, hCm) };
  }

  // Smallest real-world size (longest side, cm) a design element is ever
  // allowed to shrink to — below this, print/embroidery just isn't
  // practical. Enforced as a dynamic MIN on the Tamanho slider (see
  // updateSliderReadout) rather than a fixed scale value, since the same
  // scale means a different real cm size depending on the garment/zone/
  // layer's own aspect ratio.
  var MIN_LAYER_CM = 3;
  var SLIDER_SCALE_MIN_DEFAULT = '0.2'; // matches the <input min> in the liquid — used whenever cm can't be computed (no calibration yet)

  // The same box a layer sits in, at scale 1 — i.e. "how many cm is 100%
  // size", which is what a real-cm floor has to be computed against (a
  // fixed scale like 0.2 means a different cm size on every garment/zone).
  function layerBaseSizeCm(layer, zone, garment, imgW, imgH) {
    return layerSizeCm({ _aspect: layer._aspect, scale: 1 }, zone, garment, imgW, imgH);
  }

  // Loads just a view's garment photo (no canvas compositing) to read its
  // real pixel dimensions — needed by layerSizeCm above for every view that
  // has a design, not only the one currently on screen. Shares
  // getRecoloredSrc's own cache, so this essentially never re-fetches
  // anything already loaded elsewhere in the same session.
  function loadViewImageDims(view) {
    return getGarmentPreviewSrc(state.garment, state.color.hex, view).then(loadImage).then(function (img) {
      return { width: img.width, height: img.height };
    });
  }

  // Synchronous "how much would THIS view alone cost" estimate — used only
  // for the live info-panel display, not for the actual charge (see
  // computePrintFeeLines, which is what checkout really uses). Reads the
  // CURRENTLY DISPLAYED garment photo straight from the DOM instead of
  // loading anything, so it can run on every render without going async;
  // returns null if that image hasn't finished loading yet, or this view
  // has no design at all.
  function currentViewPrintEstimate() {
    var imgEl = document.getElementById('dt-photo-garment-img');
    if (!imgEl || !imgEl.naturalWidth) return null;
    var layers = layersForView(state.view);
    if (!layers.length) return null;
    var zone = zoneFor(state.view);
    var maxCm = 0;
    layers.forEach(function (layer) {
      var size = layerSizeCm(layer, zone, state.garment, imgEl.naturalWidth, imgEl.naturalHeight);
      if (size && size.maxCm > maxCm) maxCm = size.maxCm;
    });
    if (!maxCm) return null;
    var tier = printTierForCm(maxCm);
    return { cents: tier.priceCents, cm: maxCm };
  }

  // One fee line per view that actually has a design, each priced by that
  // view's OWN largest element (see layerSizeCm) — not one combined line
  // for the whole order. Resolves to [] for a technique/garment this
  // doesn't apply to (only Print uses size-based pricing; everything else
  // still uses selectFeeVariant's Posições-based model).
  function computePrintFeeLines() {
    if (state.technique !== 'Print') return Promise.resolve([]);
    var garment = state.garment;
    var designViews = viewKeys().filter(function (key) { return layersForView(key).length > 0; });
    return Promise.all(designViews.map(function (view) {
      return loadViewImageDims(view).then(function (dims) {
        var zone = zoneFor(view);
        var layers = layersForView(view);
        var maxCm = 0;
        layers.forEach(function (layer) {
          var size = layerSizeCm(layer, zone, garment, dims.width, dims.height);
          if (size && size.maxCm > maxCm) maxCm = size.maxCm;
        });
        var tier = printTierForCm(maxCm);
        var feeVariant = selectPrintFeeVariant(tier);
        return feeVariant && {
          id: feeVariant.id,
          priceCents: feeVariant.priceCents,
          view: view,
          viewLabel: viewLabel(garment, view),
          tierLabel: tier.optionValue,
        };
      });
    })).then(function (lines) { return lines.filter(Boolean); });
  }

  function matchedProduct(garment) {
    if (!garment || !garment.productType) return null;
    for (var i = 0; i < BLANK_PRODUCTS.length; i++) {
      if ((BLANK_PRODUCTS[i].type || '').toLowerCase() === garment.productType.toLowerCase()) return BLANK_PRODUCTS[i];
    }
    return null;
  }

  // Product option name for size, if the matched product has one (beyond just Colour).
  function sizeOptionIndex(product) {
    if (!product || !product.options) return -1;
    for (var i = 0; i < product.options.length; i++) {
      var name = (product.options[i] || '').toLowerCase();
      if (name !== 'color' && name !== 'colour') return i;
    }
    return -1;
  }

  // Colour name → hex now lives in assets/color-names.js (shared with the
  // product page's colour dropdown, see product.js) — loaded before this
  // file, see design-tool.liquid. The fallback below only matters if that
  // script somehow failed to load.
  var COLOR_NAME_HEX = window.COLOR_NAME_HEX || {};
  var hexForColorName = window.hexForColorName || function (name) {
    return COLOR_NAME_HEX[(name || '').trim().toLowerCase()] || '#9CA3AF';
  };

  // Real colours for a garment: from its matched Shopify product's variants if one
  // exists, otherwise the generic decorative palette (garment not linked to a real
  // product yet).
  function colorsForGarment(garment) {
    var product = matchedProduct(garment);
    if (!product) return GARMENT_COLORS;
    // Some real products (e.g. a hoodie sold in one colour, sizes only) have
    // no Colour option at all — nothing to pick, just show the garment's
    // one real colour instead of misreading a Size value ("S", "M"...) as if
    // it were a colour name.
    var cIdx = colorOptionIndex(product);
    if (cIdx === -1) {
      return [{ hex: garment.fixedColorHex || '#9CA3AF', name: garment.fixedColorName || 'Standard' }];
    }
    // One entry PER COLOUR, not per variant — a product with both Colour and
    // Size options has several variants sharing the same colour (one per
    // size), which would otherwise repeat that colour once per size.
    var seen = {}, colors = [];
    product.variants.forEach(function (v) {
      if (!v.available) return;
      var name = optionValue(v, cIdx) || v.title;
      if (seen[name]) return;
      seen[name] = true;
      colors.push({ hex: hexForColorName(name), name: name });
    });
    return colors;
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: SHOP_CURRENCY }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + SHOP_CURRENCY;
    }
  }

  // Up to 5 design elements (uploaded images and/or typed text) per PRINT
  // AREA — combined, not 5 of each, and each area (Front, Back, Sleeve
  // Right, ...) has its own independent set: placing something on Front
  // doesn't put it on Back too, unless the customer explicitly uses
  // "Apply to all areas". Typed text is rasterised to a PNG data URL and
  // stored exactly like an uploaded image (see renderTextToDataUrl), so both
  // kinds share one array/type and one rendering + compositing path; `type`
  // is kept only for the Layers panel's label/thumbnail and to know whether
  // the text-specific toolbar controls (font/bold/italic/align/colour) apply.
  var MAX_LAYERS = 5;

  var state = {
    garment: GARMENTS[0],
    garmentPickerCategory: null, // set while the picker is showing a category's sub-items (e.g. "cap")
    garmentConfirmed: false, // the customer must actively pick a garment tile before anything else
                             // (colour/size/technique/logo/text/layers/download/add-to-cart) unlocks
    color: colorsForGarment(GARMENTS[0])[0] || GARMENT_COLORS[0],
    size: null,
    sizeQty: {}, // { [sizeName]: quantity } — for a matched real product, an independent breakdown
                 // (not paired 1:1 with colour — see colorQty below); for a legacy garment with no
                 // matched product, the simple original single-list quantities.
    qtyTotal: null, // total quantity the customer wants, for a matched real product — colour AND size
                     // breakdowns below must each independently sum to this before Add to Cart unlocks
    colorQty: {}, // { [colourName]: quantity } — independent breakdown for a matched real product;
                  // deliberately NOT paired to specific sizes (see canAddToCart) — both are recorded as
                  // separate notes for manual production, same as this shop already does with technique/mockup
    // No technique pre-selected — the owner wants the customer to actively
    // choose Bordado/Impressão themselves rather than silently defaulting to
    // Embroidery (which used to get charged/quoted even when the customer
    // never touched the Technique panel at all). null is a real, valid
    // "nothing chosen yet" state throughout — feeProductFor(null) etc. all
    // resolve to "no technique", which addToCartBlockReason() already turns
    // into a clear "choose a technique" message once there's a design.
    technique: null,
    viewLayers: {}, // { [viewKey]: [{ id, type: 'image'|'text', url, label, scale, rotation, x, y, ...text fields }] }
    activeLayerId: null, // only meaningful within the currently active view's layer list
    view: 'front', // photo-mode garments only
    viewZones: {}, // { front: {leftPct, topPct, wPct, hPct}, ... }
    zoom: 1,
    panMode: false,
    panX: 0,
    panY: 0,
  };

  function layersForView(view) {
    if (!state.viewLayers[view]) state.viewLayers[view] = [];
    return state.viewLayers[view];
  }
  function currentLayers() { return layersForView(state.view); }
  function totalLayerCount() {
    var total = 0;
    Object.keys(state.viewLayers).forEach(function (k) { total += state.viewLayers[k].length; });
    return total;
  }

  function optionValue(variant, idx) {
    if (idx === 0) return variant.option1;
    if (idx === 1) return variant.option2;
    if (idx === 2) return variant.option3;
    return null;
  }
  function colorOptionIndex(product) {
    if (!product || !product.options) return -1;
    for (var i = 0; i < product.options.length; i++) {
      var name = (product.options[i] || '').toLowerCase();
      if (name === 'color' || name === 'colour') return i;
    }
    return -1;
  }
  // Sizes available for the garment's matched real product, at the current
  // (previewed) colour — used only for the legacy fallback flow. Returns null
  // when the garment has no matched product (caller should fall back to the
  // old hand-typed GARMENT_PRODUCTS table).
  function sizesForGarment(garment) {
    var product = matchedProduct(garment);
    if (!product) return null; // no real product at all — caller falls back to the legacy table
    var sIdx = sizeOptionIndex(product);
    if (sIdx === -1) return []; // real product exists but genuinely has no size option
    var cIdx = colorOptionIndex(product);
    var seen = {}, sizes = [];
    product.variants.forEach(function (v) {
      if (!v.available) return;
      if (cIdx !== -1 && state.color && optionValue(v, cIdx) !== state.color.name) return;
      var val = optionValue(v, sIdx);
      if (val && !seen[val]) { seen[val] = true; sizes.push(val); }
    });
    return sizes;
  }
  // Every size value the product's variants define, across every colour —
  // including out-of-stock ones (each entry says whether ANY variant in that
  // size is actually orderable) — these are the rows of the independent size
  // quantity list (see renderQtyLists). Returns [] if the product has no size
  // option (e.g. mug/bottle), null if there's no matched product at all.
  function allSizesForGarment(garment) {
    var product = matchedProduct(garment);
    if (!product) return null;
    var sIdx = sizeOptionIndex(product);
    if (sIdx === -1) return [];
    var order = [], available = {};
    product.variants.forEach(function (v) {
      var val = optionValue(v, sIdx);
      if (!val) return;
      if (order.indexOf(val) === -1) order.push(val);
      if (v.available) available[val] = true;
    });
    return order.map(function (size) { return { size: size, available: !!available[size] }; });
  }
  // Colour and size are recorded as two INDEPENDENT breakdowns (not paired
  // per unit) — e.g. "2 Red + 3 Yellow" and separately "2 L + 3 S" — the
  // exact colour/size combination per physical item is a manual-fulfilment
  // decision (this is a made-to-order shop already; see the cart submit
  // handler for how a single representative variant gets added to Shopify
  // with both breakdowns attached as line item properties).
  function colorQtyEntries(garment) {
    var product = matchedProduct(garment);
    if (!product) return null;
    return colorsForGarment(garment)
      .map(function (c) { return { name: c.name, qty: state.colorQty[c.name] || 0 }; })
      .filter(function (e) { return e.qty > 0; });
  }
  function sizeQtyEntries(garment) {
    var sizes = allSizesForGarment(garment);
    if (sizes === null) return null;
    if (!sizes.length) return []; // no size option on this product — nothing to distribute
    return sizes
      .map(function (s) { return { size: s.size, qty: state.sizeQty[s.size] || 0 }; })
      .filter(function (e) { return e.qty > 0; });
  }
  function totalColorQty(garment) {
    var entries = colorQtyEntries(garment);
    return entries ? entries.reduce(function (sum, e) { return sum + e.qty; }, 0) : 0;
  }
  function totalSizeQty(garment) {
    var entries = sizeQtyEntries(garment);
    return entries ? entries.reduce(function (sum, e) { return sum + e.qty; }, 0) : 0;
  }
  // Single source of truth for why Add to Cart is blocked right now (null
  // once nothing is blocking it) — checked in this order: (1) a garment must
  // be actively chosen first (see updateGarmentGate), (2) for a garment with
  // no matched real product, nothing further is gated (legacy single-item
  // flow); for a matched product: (3) state how many total are wanted,
  // (4) have the colour breakdown sum to that exact total, (5) have the size
  // breakdown (if the product has sizes at all) also sum to that exact
  // total, and (6) — only once at least one design element exists anywhere
  // on the garment — have a valid (priced) fee product for the chosen
  // technique, since otherwise there's no way to actually charge the
  // customisation. Shown to the customer in red next to Add to Cart (see
  // updateAddToCartState) so it's never a silent dead button.
  function addToCartBlockReason() {
    if (!state.garmentConfirmed) return t('chooseGarmentToStart');
    var g = state.garment;
    var product = matchedProduct(g);
    if (!product) return null;
    var target = state.qtyTotal;
    if (!target || target <= 0) return t('enterQtyFirst');
    // Skipped entirely when the owner has hidden that picker for this
    // garment (see renderColorPicker/renderSizePicker) — the customer has
    // no UI to distribute by colour/size in that case, so requiring it
    // would make Add to Cart permanently, silently unreachable.
    if (g.showColor !== false && totalColorQty(g) !== target) return t('distributeByColour');
    var sizes = allSizesForGarment(g);
    if (g.showSize !== false && sizes && sizes.length && totalSizeQty(g) !== target) return t('distributeBySize');
    // An order split across 2+ colours can only be split across ONE size
    // (every colour gets that same size) — with colour and size kept as
    // independent breakdowns rather than exact per-unit pairs (see the
    // comment above canAddToCart), allowing multiple of BOTH at once would
    // make it impossible to know which colour actually needs which size.
    if (g.showColor !== false && g.showSize !== false && sizes && sizes.length) {
      var colourCount = colorQtyEntries(g).length;
      var sizeCount = sizeQtyEntries(g).length;
      if (colourCount > 1 && sizeCount > 1) return t('onlyOneSizeWithMultipleColours');
    }
    if (totalLayerCount() > 0 && g.showTechnique !== false && !feeProductFor(state.technique)) return t('chooseTechniqueForDesign');
    return null;
  }
  function canAddToCart() { return !addToCartBlockReason(); }
  function updateAddToCartState() {
    var reason = addToCartBlockReason();
    els.addToCartBtn.disabled = !!reason;
    els.cartForm.classList.toggle('is-disabled', !!reason);
    if (els.cartBlockReason) els.cartBlockReason.textContent = reason || '';
    // Swap the button's own label whenever it's about to send a WhatsApp
    // quote request instead of actually adding to cart (see
    // sendEmbroideryQuoteViaWhatsapp) — same condition the submit handler
    // itself checks, kept in sync here so the button never says "Add to
    // Cart" right before doing something else entirely.
    if (els.addToCartLabelNormal && els.addToCartLabelQuote) {
      var isQuoteMode = !!state.garment && totalLayerCount() > 0 && state.garment.showTechnique !== false && state.technique === 'Embroidery';
      els.addToCartLabelNormal.hidden = isQuoteMode;
      els.addToCartLabelQuote.hidden = !isQuoteMode;
    }
  }
  // Disables every rail button/panel that needs a confirmed garment first,
  // and refreshes the Add to Cart gate (its reason text changes too).
  function updateGarmentGate() {
    document.querySelectorAll('[data-requires-garment]').forEach(function (btn) {
      btn.disabled = !state.garmentConfirmed;
    });
    updateAddToCartState();
  }
  // Since colour and size are independent breakdowns rather than exact
  // per-unit pairs (see canAddToCart), the cart still needs ONE real variant
  // id to actually add — this picks whichever is currently being previewed
  // (falling back to any available variant), and the true colour/size
  // breakdown for production is attached as line item properties instead.
  function representativeVariant(garment) {
    var product = matchedProduct(garment);
    if (!product) return null;
    var cIdx = colorOptionIndex(product);
    // Only ONE variant is ever actually added to Shopify (the real colour/
    // size split for production lives in the order's own properties — see
    // the cart-submit handler) — but that one variant still needs to be one
    // the customer genuinely picked, not an arbitrary one they never asked
    // for. Matching by colour ALONE (the previous behaviour) picked
    // whichever size variant happened to come first for that colour —
    // typically XS, by Shopify's own alphabetical variant order — showing
    // the WRONG size in the customer's own cart/checkout even when they'd
    // clearly distributed their quantity to a completely different size.
    // Confirmed live on conquistadorapparel.com: ordering 1× size L added
    // "White / XS" to the cart.
    var sIdx = sizeOptionIndex(product);
    var sizeEntries = sIdx !== -1 ? sizeQtyEntries(garment) : null;
    var preferredSize = sizeEntries && sizeEntries.length ? sizeEntries[0].size : (sIdx !== -1 ? state.size : null);
    function matches(v, requireSize) {
      if (cIdx !== -1 && optionValue(v, cIdx) !== state.color.name) return false;
      if (requireSize && sIdx !== -1 && preferredSize && optionValue(v, sIdx) !== preferredSize) return false;
      return true;
    }
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (v.available && matches(v, true)) return v;
    }
    // Fallbacks unchanged from before: colour-only match (e.g. the
    // preferred size isn't in stock, or no size was distributed at all),
    // then any available variant, then just the first — Add to Cart must
    // never be blocked just because a "perfect" match doesn't exist.
    for (var i2 = 0; i2 < product.variants.length; i2++) {
      var v2 = product.variants[i2];
      if (v2.available && matches(v2, false)) return v2;
    }
    for (var j = 0; j < product.variants.length; j++) {
      if (product.variants[j].available) return product.variants[j];
    }
    return product.variants[0] || null;
  }

  function defaultSizeFor(garment) {
    var sizes = sizesForGarment(garment);
    if (sizes) return sizes.length ? (sizes.indexOf('M') !== -1 ? 'M' : sizes[0]) : null;
    var product = GARMENT_PRODUCTS[garment.id];
    if (!product || !product.hasSizes) return null;
    var keys = Object.keys(product.sizes);
    return keys.indexOf('M') !== -1 ? 'M' : keys[0];
  }
  // One unit of the default size pre-selected (M if it exists) — so Add to
  // Cart works immediately without forcing the customer to touch the size
  // panel, same as before, just expressed as a quantity now.
  // Only pre-fills a default for the LEGACY (no matched real product) flow —
  // a matched product now requires the customer to explicitly state and
  // distribute a quantity (see canAddToCart), no silent pre-selection.
  function defaultSizeQtyFor(garment) {
    if (matchedProduct(garment)) return {};
    var sizes = sizesForGarment(garment);
    var qty = {};
    if (sizes && sizes.length) qty[sizes.indexOf('M') !== -1 ? 'M' : sizes[0]] = 1;
    return qty;
  }
  state.size = defaultSizeFor(state.garment);
  state.sizeQty = defaultSizeQtyFor(state.garment);

  var els = {
    rail: document.getElementById('dt-rail'),
    railToggle: document.getElementById('dt-rail-toggle'),
    railDownload: document.getElementById('dt-rail-download'),
    railSizeBtn: document.getElementById('dt-rail-size-btn'),
    panelArea: document.getElementById('dt-panel-area'),
    infoContent: document.getElementById('dt-info-content'),
    leftCol: document.getElementById('dt-left-col'),
    infoToggle: document.getElementById('dt-info-toggle'),
    infoClose: document.getElementById('dt-info-close'),
    undoBtn: document.getElementById('dt-undo-btn'),
    redoBtn: document.getElementById('dt-redo-btn'),
    modeToggle: document.getElementById('dt-mode-toggle'),
    garmentGrid: document.getElementById('dt-garment-grid'),
    garmentBack: document.getElementById('dt-garment-back'),
    techniqueRow: document.getElementById('dt-technique-row'),
    railTechniqueBtn: document.getElementById('dt-rail-technique-btn'),
    railColorBtn: document.getElementById('dt-rail-color-btn'),
    colorRow: document.getElementById('dt-color-row'),
    colorName: document.getElementById('dt-selected-color-name'),
    sizeRow: document.getElementById('dt-size-row'),
    viewTabs: document.getElementById('dt-view-tabs'),
    canvasViewport: document.getElementById('dt-canvas-viewport'),
    canvasStage: document.getElementById('dt-canvas-stage'),
    canvasWrap: document.getElementById('dt-canvas-wrap'),
    logoTrigger: document.getElementById('dt-logo-trigger'),
    logoInput: document.getElementById('dt-logo-input'),
    layersList: document.getElementById('dt-layers-list'),
    layersCount: document.getElementById('dt-layers-count'),
    layerControls: document.getElementById('dt-layer-controls'),
    elementToolbar: document.getElementById('dt-element-toolbar'),
    sliderScale: document.getElementById('dt-slider-scale'),
    sliderScaleReadout: document.getElementById('dt-slider-scale-readout'),
    sliderRotation: document.getElementById('dt-slider-rotation'),
    textInput: document.getElementById('dt-text-input'),
    textFont: document.getElementById('dt-text-font'),
    textColor: document.getElementById('dt-text-color'),
    textAdd: document.getElementById('dt-text-add'),
    downloadBtn: document.getElementById('dt-download'),
    cartForm: document.getElementById('dt-cart-form'),
    addToCartBtn: document.getElementById('dt-add-to-cart'),
    cartBlockReason: document.getElementById('dt-cart-block-reason'),
    variantInput: document.getElementById('dt-variant-id'),
    propGarment: document.getElementById('dt-prop-garment'),
    propTechnique: document.getElementById('dt-prop-technique'),
    propColour: document.getElementById('dt-prop-colour'),
    propPosition: document.getElementById('dt-prop-position'),
    propFile: document.getElementById('dt-prop-design-file'),
    propFilesExtra: document.getElementById('dt-prop-design-files-extra'),
    whatsappCta: document.getElementById('dt-whatsapp-cta'),
    bottomBarTotal: document.getElementById('dt-bottom-bar-total'),
    addToCartLabelNormal: document.getElementById('dt-add-to-cart-label-normal'),
    addToCartLabelQuote: document.getElementById('dt-add-to-cart-label-quote'),
    toast: document.getElementById('dt-toast'),
    exportCanvas: document.getElementById('dt-export-canvas'),
    zoomOut: document.getElementById('dt-zoom-out'),
    zoomIn: document.getElementById('dt-zoom-in'),
    zoomPct: document.getElementById('dt-zoom-pct'),
    panToggle: document.getElementById('dt-pan-toggle'),
    tutWelcome: document.getElementById('dt-tut-welcome'),
    wizard: document.getElementById('dt-mobile-wizard'),
    wizardStep: document.getElementById('dt-mobile-wizard-step'),
    wizardSkip: document.getElementById('dt-mobile-wizard-skip'),
    wizardBody: document.getElementById('dt-mobile-wizard-body'),
    wizardQty: document.getElementById('dt-mobile-wizard-qty'),
    wizardQtyInput: document.getElementById('dt-mobile-wizard-qty-input'),
    wizardQtyNext: document.getElementById('dt-mobile-wizard-qty-next'),
    tutAccept: document.getElementById('dt-tut-accept'),
    tutDecline: document.getElementById('dt-tut-decline'),
    tutHelpBtn: document.getElementById('dt-tut-help-btn'),
    tutSpotlight: document.getElementById('dt-tut-spotlight'),
    tutBalloon: document.getElementById('dt-tut-balloon'),
    tutStepCount: document.getElementById('dt-tut-step-count'),
    tutBalloonTitle: document.getElementById('dt-tut-balloon-title'),
    tutBalloonText: document.getElementById('dt-tut-balloon-text'),
    tutBack: document.getElementById('dt-tut-back'),
    tutNext: document.getElementById('dt-tut-next'),
    tutSkip: document.getElementById('dt-tut-skip'),
  };

  // ─── Translations ──────────────────────────────────────────────────────
  // All dynamically-generated (JS-built) UI copy in this tool, bilingual —
  // static markup in design-tool.liquid already uses the site-wide
  // [data-lang-pt]/[data-lang-en] pattern; this covers what only exists as
  // strings in here. Reads the language lang-toggle.js (assets/lang-toggle.js)
  // sets on <html data-lang>, and re-renders on its 'langchange' event so a
  // switch mid-session updates whatever's already on screen.
  var STRINGS = {
    chooseGarmentToStart: { pt: 'Escolha um artigo para começar.', en: 'Choose a product to get started.' },
    enterQtyFirst: { pt: 'Indique a quantidade pretendida em Informação do Produto.', en: 'Enter how many you’d like in Product Information.' },
    distributeByColour: { pt: 'Distribua a quantidade total por cor.', en: 'Distribute the full quantity by colour.' },
    distributeBySize: { pt: 'Distribua a quantidade total por tamanho.', en: 'Distribute the full quantity by size.' },
    onlyOneSizeWithMultipleColours: { pt: 'Com mais de uma cor escolhida, só pode escolher um tamanho para todas.', en: 'With more than one colour chosen, you can only pick a single size for all of them.' },
    chooseTechniqueForDesign: { pt: 'Escolha uma técnica para incluir o seu design.', en: 'Choose a technique to include your design.' },
    setQtyFirst: { pt: 'Indique primeiro uma quantidade em Informação do Produto', en: 'Set a quantity in Product Information first' },
    noStock: { pt: 'Sem stock', en: 'No stock' },
    size: { pt: 'Tamanho', en: 'Size' },
    distributedBySize: { pt: '{done} de {total} distribuído por tamanho', en: '{done} of {total} distributed by size' },
    singleSizeWithMultipleColoursHint: { pt: 'Escolheu mais de uma cor — só pode indicar um tamanho, para todas.', en: 'You picked more than one colour — you can only set a single size, for all of them.' },
    distributedByColour: { pt: '{done} de {total} distribuído por cor', en: '{done} of {total} distributed by colour' },
    previewColour: { pt: 'Pré-visualizar {name}', en: 'Preview {name}' },
    notLinkedYet: { pt: 'Esta peça ainda não está ligada a um produto da loja — o preço será confirmado quando dermos o orçamento da sua encomenda.', en: 'This piece isn’t linked to a store product yet — pricing will be confirmed when we quote your order.' },
    garment: { pt: 'Peça', en: 'Garment' },
    colour: { pt: 'Cor', en: 'Colour' },
    technique: { pt: 'Técnica', en: 'Technique' },
    // The two known techniques' on-screen NAME, as opposed to the `technique`
    // key above (the field label "Technique:"). state.technique itself always
    // holds the canonical English value ("Embroidery"/"Print" — it has to,
    // that's what matches window.DT_FEE_PRODUCTS' keys and what's sent as
    // properties[Technique] to Shopify) — displaying it straight to a PT
    // customer would show the English word are Portuguese sentence, e.g.
    // "Personalização (Embroidery)". See techniqueLabel() below, used
    // everywhere state.technique reaches the screen instead of a cart
    // property. Any OTHER technique an owner tags "Technique" in the admin
    // still shows as its raw product title — there's no way to know its PT
    // name ahead of time (same as the segmented button in the liquid, which
    // only hand-translates these same two).
    techniqueEmbroidery: { pt: 'Bordado', en: 'Embroidery' },
    techniquePrint: { pt: 'Impressão', en: 'Print' },
    embroideryQuoteShareNote: { pt: 'As imagens do meu design seguem em anexo.', en: 'My design images are attached.' },
    embroideryQuoteShared: { pt: 'Pronto a enviar — escolha o WhatsApp na lista para partilhar já com as imagens.', en: 'Ready to send — pick WhatsApp from the list to share it with the images already attached.' },
    // Mobile questionnaire step labels — kept as their own STRINGS entries
    // (rather than reading the reparented panel's own <h2> text) because
    // that title element holds BOTH the PT and EN <span>s at once (only one
    // shown via [hidden], toggled by lang-toggle.js) — .textContent ignores
    // the hidden attribute and would return both languages concatenated.
    wizardStepGarment: { pt: 'Escolher Artigo', en: 'Choose Product' },
    wizardStepColour: { pt: 'Cor da Peça', en: 'Garment Colour' },
    inStock: { pt: 'Em stock', en: 'In stock' },
    howManyWouldYouLike: { pt: 'Quantas gostaria de encomendar?', en: 'How many would you like?' },
    distributedByColourCheck: { pt: '{done} de {total} distribuído por cor{check}', en: '{done} of {total} distributed by colour{check}' },
    distributedBySizeCheck: { pt: '{done} de {total} distribuído por tamanho{check}', en: '{done} of {total} distributed by size{check}' },
    enterQtyThenDistribute: { pt: 'Indique uma quantidade e distribua-a por cor (e tamanho, se aplicável) nos respetivos separadores.', en: 'Enter a quantity, then distribute it by colour (and size, if applicable) in their tabs.' },
    blankGarment: { pt: 'Peça em branco', en: 'Blank garment' },
    each: { pt: ' (cada)', en: ' (each)' },
    eachShort: { pt: ', cada', en: ', each' },
    customisation: { pt: 'Personalização', en: 'Customisation' },
    addDesignToInclude: { pt: 'Adicione um design para incluir', en: 'Add a design to include' },
    quoteOnly: { pt: 'Sob consulta (WhatsApp)', en: 'Quote only (WhatsApp)' },
    printThisView: { pt: 'nesta vista, {cm}cm', en: 'this view, {cm}cm' },
    printPerViewNote: { pt: 'Cada vista com desenho é cobrada pelo seu próprio tamanho — o total certo aparece já no carrinho.', en: 'Each view with a design is charged by its own size — the exact total shows up in the cart.' },
    measuringSize: { pt: 'A medir o tamanho…', en: 'Measuring size…' },
    quantity: { pt: 'Quantidade', en: 'Quantity' },
    total: { pt: 'Total', en: 'Total' },
    colours: { pt: 'Cores', en: 'Colours' },
    sizes: { pt: 'Tamanhos', en: 'Sizes' },
    elementsUsed: { pt: '{count} de {max} elementos usados (nesta área)', en: '{count} of {max} elements used (this area)' },
    noElementsYet: { pt: 'Ainda não há elementos nesta área — adicione um logótipo ou texto para começar.', en: 'No elements on this area yet — add a logo or text to get started.' },
    layerText: { pt: 'Texto', en: 'Text' },
    layerImage: { pt: 'Imagem', en: 'Image' },
    moveUp: { pt: 'Mover para cima', en: 'Move up' },
    moveDown: { pt: 'Mover para baixo', en: 'Move down' },
    delete: { pt: 'Eliminar', en: 'Delete' },
    uploadHint: { pt: 'Carregue um logótipo ou adicione texto para o colocar aqui', en: 'Upload a logo or add text to place it here' },
    position: { pt: 'Posição', en: 'Position' },
    sizePct: { pt: 'Tamanho %', en: 'Size %' },
    bold: { pt: 'Negrito', en: 'Bold' },
    italic: { pt: 'Itálico', en: 'Italic' },
    alignLeft: { pt: 'Alinhar à esquerda', en: 'Align left' },
    alignCenter: { pt: 'Alinhar ao centro', en: 'Align center' },
    alignRight: { pt: 'Alinhar à direita', en: 'Align right' },
    duplicate: { pt: 'Duplicar', en: 'Duplicate' },
    removeBackground: { pt: 'Remover Fundo', en: 'Remove Background' },
    removingBackground: { pt: 'A remover fundo…', en: 'Removing background…' },
    backgroundRemoved: { pt: 'Fundo removido.', en: 'Background removed.' },
    couldNotRemoveBackground: { pt: 'Não foi possível remover o fundo. Tente novamente.', en: 'Could not remove the background. Please try again.' },
    maxLayersAdd: { pt: 'Máximo de {max} elementos atingido para esta área — elimine um para adicionar outro.', en: 'Maximum of {max} elements reached for this area — delete one to add another.' },
    maxLayersDuplicate: { pt: 'Máximo de {max} elementos atingido para esta área — elimine um para duplicar.', en: 'Maximum of {max} elements reached for this area — delete one to duplicate.' },
    addedDragToReposition: { pt: 'Adicionado — arraste na pré-visualização para reposicionar.', en: 'Added — drag it on the preview to reposition it.' },
    couldNotReadFile: { pt: 'Não foi possível ler esse ficheiro.', en: 'Could not read that file.' },
    typeSomethingFirst: { pt: 'Escreva algo primeiro.', en: 'Type something first.' },
    couldNotRenderText: { pt: 'Não foi possível gerar esse texto — tente outra fonte.', en: 'Could not render that text — try a different font.' },
    couldNotGenerateMockup: { pt: 'Não foi possível gerar a imagem.', en: 'Could not generate the mockup image.' },
    couldNotAddToCart: { pt: 'Não foi possível adicionar ao carrinho. Tente novamente.', en: 'Could not add this to your cart. Please try again.' },
    chooseSizeFirst: { pt: 'Escolha um tamanho antes de adicionar ao carrinho.', en: 'Please choose a size before adding to cart.' },
    notAvailableOnline: { pt: 'Este artigo ainda não está disponível para encomendar online — envie-nos uma mensagem no WhatsApp e nós tratamos disso.', en: 'This item isn’t available to order online yet — message us on WhatsApp and we’ll sort it out.' },
    addingToCart: { pt: 'A adicionar ao carrinho…', en: 'Adding to cart…' },
    whatsappQuoteText: { pt: 'Olá! Desenhei uma peça {colour} {garment} com o meu logótipo na vossa ferramenta de design. Gostaria de pedir um orçamento!', en: 'Hi! I’ve designed a {colour} {garment} with my logo using your design tool. I’d like to get a quote!' },
    // Bordado (Embroidery) has no fixed online price — see
    // sendEmbroideryQuoteViaWhatsapp for why these build a WhatsApp message
    // instead of a normal cart line.
    embroideryQuoteIntro: { pt: 'Olá! Gostaria de pedir um orçamento para bordado.', en: 'Hi! I’d like to request an embroidery quote.' },
    embroideryQuoteGarment: { pt: 'Peça: {garment}', en: 'Item: {garment}' },
    embroideryQuoteColour: { pt: 'Cores: {breakdown}', en: 'Colours: {breakdown}' },
    embroideryQuoteSize: { pt: 'Tamanhos: {breakdown}', en: 'Sizes: {breakdown}' },
    embroideryQuotePositions: { pt: 'Nº de posições com desenho: {count}', en: 'Number of positions with a design: {count}' },
    embroideryQuoteAttachNote: { pt: 'Vou enviar de seguida as imagens do meu design (acabaram de ser descarregadas para o meu dispositivo).', en: 'I’ll attach my design images next (they were just downloaded to my device).' },
    preparingQuote: { pt: 'A preparar o pedido de orçamento…', en: 'Preparing your quote request…' },
    embroideryQuoteSent: { pt: 'WhatsApp aberto — anexe as imagens descarregadas e envie a mensagem.', en: 'WhatsApp opened — attach the downloaded images and send the message.' },
    yourDesignYourStyle: { pt: 'O seu design. O seu estilo. Bordado com orgulho.', en: 'Your design. Your style. Embroidered with pride.' },
    tutNext: { pt: 'Seguinte', en: 'Next' },
    tutFinish: { pt: 'Concluir', en: 'Finish' },
    tutStepOf: { pt: 'Passo {n} de {total}', en: 'Step {n} of {total}' },
    tutStep1Title: { pt: 'Escolha o artigo', en: 'Choose your product' },
    tutStep1Text: { pt: 'Comece por escolher o que quer personalizar — t-shirt, boné, caneca, e mais.', en: 'Start by picking what you want to customise — t-shirt, cap, mug, and more.' },
    tutStep2Title: { pt: 'Escolha a cor', en: 'Pick a colour' },
    tutStep2Text: { pt: 'Depois de escolher a peça, escolha a cor aqui. A pré-visualização atualiza-se na hora.', en: 'Once you\'ve picked a garment, choose its colour here. The preview updates instantly.' },
    tutStep3Title: { pt: 'Técnica', en: 'Technique' },
    tutStep3Text: { pt: 'Escolha entre bordado ou impressão para o seu design.', en: 'Choose between embroidery or print for your design.' },
    tutStep4Title: { pt: 'Carregue o seu logótipo', en: 'Upload your logo' },
    tutStep4Text: { pt: 'Carregue uma imagem sua. Depois de a colocar, pode remover o fundo automaticamente com um clique.', en: 'Upload your own image. Once it\'s placed, you can remove its background automatically with one click.' },
    tutStep5Title: { pt: 'Ou adicione texto', en: 'Or add text' },
    tutStep5Text: { pt: 'Sem ficheiro? Escreva o seu texto, escolha a fonte e a cor.', en: 'No file needed? Type your text and pick a font and colour.' },
    tutStep6Title: { pt: 'Posicione no design', en: 'Position it on the design' },
    tutStep6Text: { pt: 'Arraste diretamente na pré-visualização para posicionar. Num telemóvel ou ecrã tátil, pode ainda aumentar/diminuir e rodar com dois dedos, como numa fotografia — ou use os controlos na aba Camadas.', en: 'Drag directly on the preview to position it. On a phone or touchscreen, you can also pinch to resize and twist to rotate with two fingers, just like a photo — or use the Layers tab\'s controls.' },
    tutStep7Title: { pt: 'Está pronto?', en: 'All set?' },
    tutStep7Text: { pt: 'Quando estiver satisfeito, adicione ao carrinho — se escolheu Bordado, o botão pede antes um orçamento pelo WhatsApp, já que o bordado é sempre sob consulta. Pode voltar a abrir este tutorial no botão de ajuda (?) ali em cima.', en: 'When you\'re happy with it, add it to your cart — if you picked Embroidery, the button asks for a WhatsApp quote instead, since embroidery pricing is always quote-only. You can reopen this tutorial from the help (?) button up top.' },
  };
  function currentLang() {
    return document.documentElement.getAttribute('data-lang') === 'en' ? 'en' : 'pt';
  }
  function t(key, vars) {
    var entry = STRINGS[key];
    var str = entry ? (entry[currentLang()] || entry.en) : key;
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        str = str.split('{' + k + '}').join(vars[k]);
      });
    }
    return str;
  }
  // Customer-facing NAME for a technique — state.technique/properties[Technique]
  // etc. must stay the raw canonical value ("Embroidery"/"Print", matching
  // window.DT_FEE_PRODUCTS' keys and the Shopify product title), so every
  // spot that puts it on screen instead of into a cart property needs to go
  // through this first. Anything other than the two known techniques (an
  // extra "Technique"-tagged product the owner adds later) has no PT name to
  // fall back to, same as the segmented button in the liquid — shown as-is.
  function techniqueLabel(technique) {
    if (technique === 'Embroidery') return t('techniqueEmbroidery');
    if (technique === 'Print') return t('techniquePrint');
    return technique;
  }

  // Asset filenames in garment data are plain filenames (e.g. "cap-vintage-front.png");
  // resolve them against the theme's CDN asset folder (derived from this section's own
  // script tag) so no Liquid needs to run inside these static JS files.
  //
  // The folder's own query string (e.g. "?v=1786...", garments-data.js's own
  // Liquid-generated cache-busting fingerprint) used to be thrown away here —
  // that meant every bare theme-asset filename was requested with NO version
  // param at all, at the mercy of the CDN's very long cache (max-age is
  // ~1 year). Overwriting an existing asset (same filename, new bytes — e.g.
  // swapping in a sharper garment photo) then silently kept serving the OLD
  // cached file to real customers, sometimes for up to a year, with no error
  // anywhere to notice it by. Reusing that fingerprint as a suffix instead
  // means any edit to garments-data.js (which is required to reference a
  // changed image anyway) naturally busts the cache for every asset it
  // points at, not just the one being viewed.
  var assetBaseInfo = (function () {
    var raw = root.dataset.assetBase || '';
    var qIdx = raw.indexOf('?');
    return {
      base: raw.split('?')[0].replace('garments-data.js', ''),
      version: qIdx >= 0 ? raw.slice(qIdx) : '',
    };
  })();
  var assetBase = assetBaseInfo.base;
  // Garments added via the Chrome extension point straight at the product's
  // own Shopify CDN photo (a full URL) instead of a theme asset filename —
  // pass those through unchanged rather than mangling them into
  // assetBase + "https://...". Also matches a protocol-relative "//..."
  // URL: Shopify's own asset pipeline silently rewrites a bare filename
  // that matches a real theme asset (e.g. "mug-wrap-original.png") into a
  // full "//shop.com/cdn/.../mug-wrap-original.png" reference INSIDE the
  // served JS's string literals — even though the source file on disk still
  // has the plain filename. Without this, that already-resolved value fails
  // the old https?:// -only check and gets assetBase prepended a second
  // time, producing a broken double-resolved URL.
  function resolveAsset(filename) { return /^(?:https?:)?\/\//.test(filename) ? filename : assetBase + filename + assetBaseInfo.version; }

  // A garment/category icon is normally a plain emoji character, but the
  // Chrome extension's icon picker can also store a real uploaded image
  // URL (https://...) or one of its own built-in quick-pick icons (a
  // data:image/svg+xml;base64,... URI, bundled directly in the extension,
  // never uploaded anywhere) — both render as a small <img> instead of
  // emoji text. `esc` guards against an icon value ever breaking out of
  // the attribute (a URL/data-URI is the only externally-suppliable value
  // here).
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  function isIconImage(icon) { return /^(https?:|data:)/i.test(icon); }
  function iconHtml(icon) {
    return isIconImage(icon)
      ? '<img class="emoji emoji--img" src="' + esc(icon) + '" alt="">'
      : '<span class="emoji">' + icon + '</span>';
  }
  // For the couple of spots that set an icon via .textContent on an
  // existing element rather than building markup — same emoji-vs-image
  // logic, just applied in place.
  function setIconInto(el, icon) {
    if (isIconImage(icon)) el.innerHTML = '<img class="emoji emoji--img" src="' + esc(icon) + '" alt="">';
    else el.textContent = icon;
  }

  // Tiny inline-SVG icons for JS-generated markup (the Layers list rows) —
  // mirrors the matching cases in snippets/icon.liquid, which can't be
  // rendered from here since this is a plain static JS file.
  var ICON_PATHS = {
    'chevron-up': '<polyline points="18 15 12 9 6 15"/>',
    'chevron-down': '<polyline points="6 9 12 15 18 9"/>',
    trash: '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
    copy: '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
    'align-left': '<line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="18" y2="18"/>',
    'align-center': '<line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="5" y1="18" x2="19" y2="18"/>',
    'align-right': '<line x1="4" y1="6" x2="20" y2="6"/><line x1="10" y1="12" x2="20" y2="12"/><line x1="6" y1="18" x2="20" y2="18"/>',
  };
  function iconMarkup(name) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICON_PATHS[name] || '') + '</svg>';
  }

  function showToast(message, type) {
    els.toast.textContent = message;
    els.toast.className = 'dt-toast is-visible' + (type ? ' is-' + type : '');
    clearTimeout(showToast._t);
    showToast._t = setTimeout(function () {
      els.toast.classList.remove('is-visible');
    }, 3200);
  }

  function loadImageEl(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
  }

  // ─── Colour helpers ────────────────────────────────────────────────────
  function hexToRgb(hex) {
    var v = parseInt(hex.replace('#', ''), 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  }
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var l = (max + min) / 2, d = max - min, h = 0, s = 0;
    if (d !== 0) {
      s = d / (1 - Math.abs(2 * l - 1));
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
      if (h < 0) h += 360;
    }
    return [h, s, l];
  }
  function hslToRgb(h, s, l) {
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    var m = l - c / 2;
    var r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
    return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
  }
  function lightnessOf(hex) {
    var rgb = hexToRgb(hex);
    return (Math.max.apply(null, rgb) + Math.min.apply(null, rgb)) / 2 / 255;
  }

  // ─── Vector garment rendering ──────────────────────────────────────────
  // Builds a full, self-contained SVG string for a vector garment at a given
  // colour: fixed layers (e.g. a trucker cap's mesh) keep their own fill;
  // recolourable layers get the chosen colour; detail line art (stitching,
  // drawstrings, seams) is drawn with stroke/fill="currentColor", resolved via
  // an inline `style="color:..."` on the <svg> root so it stays legible on any
  // garment colour, light or dark.
  function buildVectorGarmentSvg(garment, view, colorHex) {
    var viewData = garment.views[view] || garment.views[Object.keys(garment.views)[0]];
    var vb = garment.viewBox.split(' ').map(Number);
    // colorHex is optional so this can be called outside a live customer
    // session (see window.DT_renderVectorGarmentView below) — falls back to
    // whatever colour the customer currently has selected.
    var color = colorHex || state.color.hex;
    var strokeColor = lightnessOf(color) > 0.55 ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.9)';
    // Stroke width is in viewBox units, so a garment traced on a larger
    // canvas (e.g. the photo-traced tshirt at 862x764) needs a proportionally
    // thicker value to read the same on screen as one drawn on a 400-wide box.
    var strokeWidth = garment.strokeWidth || 2.5;
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + garment.viewBox +
      '" width="' + vb[2] + '" height="' + vb[3] +
      '" style="color:' + strokeColor + '" preserveAspectRatio="xMidYMid meet">';
    (viewData.fixedLayers || []).forEach(function (layer) {
      svg += '<path d="' + layer.path + '" fill="' + layer.fill + '" stroke="none"/>';
    });
    (viewData.recolorLayers || []).forEach(function (layer) {
      svg += '<path d="' + layer.path + '" fill="' + color +
        '" stroke="currentColor" stroke-width="' + strokeWidth + '" stroke-linejoin="round" stroke-linecap="round"/>';
    });
    svg += '<g fill="none" stroke="currentColor" stroke-width="' + strokeWidth + '" stroke-linejoin="round" stroke-linecap="round">' +
      (viewData.details || '') + '</g>';
    svg += '</svg>';
    return svg;
  }

  function svgToDataUri(svgMarkup) {
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgMarkup)));
  }

  // Exposed purely for the "Painel do Editor" Chrome extension (see
  // page-bridge.js): lets it show the REAL per-view illustration (Front vs
  // Back really do have different traced artwork for most garments) in its
  // zone editor, instead of falling back to the small referenceImage fabric
  // photo — which is always the same "front" photo regardless of view, and
  // was showing as an identical Front/Back preview there.
  window.DT_renderVectorGarmentView = function (garmentId, view) {
    var garment = (window.GARMENTS_VECTOR || []).find(function (g) { return g.id === garmentId; });
    if (!garment || !garment.views) return null;
    var neutralHex = (window.GARMENT_COLORS && window.GARMENT_COLORS[0] && window.GARMENT_COLORS[0].hex) || '#FFFFFF';
    return svgToDataUri(buildVectorGarmentSvg(garment, view, neutralHex));
  };

  // ─── Photo garment recolouring (mug / bottle) ─────────────────────────
  // Bakes the chosen colour directly into the product photo while keeping its
  // real shading: each fabric/surface pixel's own lightness is measured,
  // recentred around the target colour's own lightness, and the target's
  // hue/saturation applied on top — folds and shadows stay visible at any
  // target colour, including black or fully saturated ones. A masked box
  // blur over the lightness map first smooths out any fine high-contrast
  // noise in the source photo (e.g. a mottled/washed material) so it reads
  // as soft shading rather than harsh blotches.
  var recolorCache = {};
  function maskedBlurL(L, isBackdrop, W, H, R) {
    var valH = new Float64Array(W * H), cntH = new Float32Array(W * H);
    var x, y, idx, sum, cnt, xOut, xIn, idxOut, idxIn;
    for (y = 0; y < H; y++) {
      sum = 0; cnt = 0;
      for (x = -R; x <= R; x++) {
        idx = y * W + Math.min(W - 1, Math.max(0, x));
        if (!isBackdrop[idx]) { sum += L[idx]; cnt++; }
      }
      for (x = 0; x < W; x++) {
        valH[y * W + x] = sum; cntH[y * W + x] = cnt;
        xOut = x - R; xIn = x + R + 1;
        if (xOut >= 0) {
          idxOut = y * W + Math.min(W - 1, Math.max(0, xOut));
          if (!isBackdrop[idxOut]) { sum -= L[idxOut]; cnt--; }
        }
        if (xIn < W) {
          idxIn = y * W + Math.min(W - 1, Math.max(0, xIn));
          if (!isBackdrop[idxIn]) { sum += L[idxIn]; cnt++; }
        }
      }
    }
    var valV = new Float64Array(W * H), cntV = new Float32Array(W * H);
    var yOut, yIn;
    for (x = 0; x < W; x++) {
      sum = 0; cnt = 0;
      for (y = -R; y <= R; y++) {
        idx = Math.min(H - 1, Math.max(0, y)) * W + x;
        sum += valH[idx]; cnt += cntH[idx];
      }
      for (y = 0; y < H; y++) {
        valV[y * W + x] = sum; cntV[y * W + x] = cnt;
        yOut = y - R; yIn = y + R + 1;
        if (yOut >= 0) { idxOut = Math.min(H - 1, Math.max(0, yOut)) * W + x; sum -= valH[idxOut]; cnt -= cntH[idxOut]; }
        if (yIn < H) { idxIn = Math.min(H - 1, Math.max(0, yIn)) * W + x; sum += valH[idxIn]; cnt += cntH[idxIn]; }
      }
    }
    var out = new Float64Array(W * H);
    for (var p = 0; p < W * H; p++) out[p] = cntV[p] > 0 ? valV[p] / cntV[p] : L[p];
    return out;
  }

  function getRecoloredSrc(url, colorHex, blurRadius, keepWhiteZonePct) {
    // blurRadius smooths photographed-fabric texture noise so it reads as
    // soft shading rather than harsh blotches — but on FLAT artwork (a print
    // template's crisp dashed lines/grid, not a photo) that same smoothing
    // just blurs fine detail into mush, so callers pass 0 for those (see
    // garment.flatArtwork in garments-data.js).
    if (blurRadius == null) blurRadius = 4;
    // keepWhiteZonePct ({xPct,yPct,wPct,hPct}, e.g. Mug's print area):
    // that rectangle is forced to stay pure opaque white below, skipping
    // both the backdrop flood-fill and the colour-tint pass entirely —
    // needed because a print zone fully enclosed by a solid border (not
    // connected to the true backdrop through any gap) would otherwise get
    // swept up as part of "the object" and tinted along with it.
    var cacheKey = url + '::' + colorHex + '::' + blurRadius + '::' + (keepWhiteZonePct ? JSON.stringify(keepWhiteZonePct) : '');
    if (!recolorCache[cacheKey]) {
      recolorCache[cacheKey] = loadImageEl(url).then(function (img) {
        var W = img.naturalWidth, H = img.naturalHeight;
        var canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        var ctx = canvas.getContext('2d');
        // Draw once WITHOUT flattening first, purely to check whether this
        // source actually carries real alpha transparency (a cutout mockup
        // PNG/WEBP with a see-through background, as opposed to a photo
        // shot against an opaque studio backdrop) — if it does, that alpha
        // channel is a pixel-perfect, unambiguous garment/backdrop boundary,
        // and is used directly below instead of the luminance flood-fill.
        // That flood-fill (kept as the fallback for ordinary opaque photos)
        // assumes a brightness *gap* between the object and the backdrop to
        // stop tunnelling at — a white garment shot/rendered against a
        // white backdrop with little natural shading (common for flat mockup
        // generator exports) has no such gap, so it tunnels straight through
        // the garment itself, eating jagged holes out of it.
        ctx.drawImage(img, 0, 0);
        var rawData = ctx.getImageData(0, 0, W, H).data;
        var hasAlpha = false;
        for (var ai = 3; ai < rawData.length; ai += 4) {
          if (rawData[ai] < 250) { hasAlpha = true; break; }
        }

        // Flatten onto white for the colour/luminance work below — source
        // art may have a transparent background rather than an actual white
        // studio backdrop — without this, transparent pixels read as black
        // (RGB 0,0,0) and get painted in instead of staying see-through.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, W, H);
        ctx.drawImage(img, 0, 0);
        var imageData = ctx.getImageData(0, 0, W, H);
        var d = imageData.data;
        var BACKDROP_THRESHOLD = 250;
        var BLUR_RADIUS = blurRadius;

        function lumAt(px) {
          var p = px * 4;
          return (d[p] + d[p + 1] + d[p + 2]) / 3;
        }

        var isBackdrop = new Uint8Array(W * H);
        if (hasAlpha) {
          for (var api = 0; api < W * H; api++) {
            isBackdrop[api] = rawData[api * 4 + 3] < 128 ? 1 : 0;
          }
        } else {
          // Flood-fill the studio backdrop starting from the image edges —
          // only pixels actually *connected* to the outer border count as
          // background, so bright highlights in the middle of the object
          // aren't mistaken for backdrop and left uncoloured.
          var visited = new Uint8Array(W * H);
          var stack = [];
          for (var x = 0; x < W; x++) { stack.push(x); stack.push((H - 1) * W + x); }
          for (var y = 0; y < H; y++) { stack.push(y * W); stack.push(y * W + W - 1); }
          while (stack.length) {
            var idx = stack.pop();
            if (visited[idx]) continue;
            visited[idx] = 1;
            if (lumAt(idx) < BACKDROP_THRESHOLD) continue;
            isBackdrop[idx] = 1;
            var px = idx % W, py = (idx / W) | 0;
            if (px > 0) stack.push(idx - 1);
            if (px < W - 1) stack.push(idx + 1);
            if (py > 0) stack.push(idx - W);
            if (py < H - 1) stack.push(idx + W);
          }

          // Close up thin 1-2px leaks in the backdrop mask — a bright
          // specular highlight (e.g. a shiny drawstring grommet or a seam
          // catching the studio light) can be just bright enough, and just
          // barely touch the true backdrop, for the flood fill above to
          // tunnel a hairline crack through it into the garment; a pixel
          // almost entirely surrounded by real garment is reclaimed rather
          // than left as a stray transparent speck. Two passes fully seals
          // narrow tunnels a single pass would only partially close. Not
          // needed for the alpha-based mask above — its edge is already
          // exact, nothing to reclaim.
          for (var pass = 0; pass < 2; pass++) {
            var eroded = new Uint8Array(isBackdrop);
            for (var ey = 0; ey < H; ey++) {
              for (var ex = 0; ex < W; ex++) {
                var eidx = ey * W + ex;
                if (!isBackdrop[eidx]) continue;
                var neighborBackdrop = 0, neighborTotal = 0;
                for (var dy = -1; dy <= 1; dy++) {
                  for (var dx = -1; dx <= 1; dx++) {
                    if (dx === 0 && dy === 0) continue;
                    var nx = ex + dx, ny = ey + dy;
                    if (nx < 0 || nx >= W || ny < 0 || ny >= H) continue;
                    neighborTotal++;
                    if (isBackdrop[ny * W + nx]) neighborBackdrop++;
                  }
                }
                if (neighborTotal > 0 && neighborBackdrop / neighborTotal < 0.55) eroded[eidx] = 0;
              }
            }
            isBackdrop = eroded;
          }
        }

        // Pixels inside keepWhiteZonePct are pinned to opaque white further
        // down instead of being tinted — folded into the SAME exclusion
        // mask the blur and the mean-brightness calc use, so that patch
        // neither bleeds its brightness into the tinted area's blur nor
        // skews what "average brightness" means for the rest of the print.
        var keepWhite = null;
        if (keepWhiteZonePct) {
          keepWhite = new Uint8Array(W * H);
          var zx0 = Math.max(0, Math.round((keepWhiteZonePct.xPct / 100) * W));
          var zy0 = Math.max(0, Math.round((keepWhiteZonePct.yPct / 100) * H));
          var zx1 = Math.min(W, Math.round(((keepWhiteZonePct.xPct + keepWhiteZonePct.wPct) / 100) * W));
          var zy1 = Math.min(H, Math.round(((keepWhiteZonePct.yPct + keepWhiteZonePct.hPct) / 100) * H));
          for (var zy = zy0; zy < zy1; zy++) {
            for (var zx = zx0; zx < zx1; zx++) keepWhite[zy * W + zx] = 1;
          }
        }
        var excludeMask = isBackdrop;
        if (keepWhite) {
          excludeMask = new Uint8Array(W * H);
          for (var em = 0; em < W * H; em++) excludeMask[em] = (isBackdrop[em] || keepWhite[em]) ? 1 : 0;
        }

        var rgbMatch = colorHex.match(/[0-9a-f]{2}/gi).map(function (h) { return parseInt(h, 16); });
        var target = rgbToHsl(rgbMatch[0], rgbMatch[1], rgbMatch[2]);

        var L = new Float64Array(W * H);
        for (var i = 0, p = 0; i < d.length; i += 4, p++) {
          L[p] = rgbToHsl(d[i], d[i + 1], d[i + 2])[2];
        }
        var Lsmooth = maskedBlurL(L, excludeMask, W, H, BLUR_RADIUS);

        var sumL = 0, count = 0;
        for (p = 0; p < W * H; p++) {
          if (excludeMask[p]) continue;
          sumL += Lsmooth[p];
          count++;
        }
        var meanL = count > 0 ? sumL / count : 0.5;

        // Re-lighting used to be `target[2] + (Lsmooth - meanL)`, hard-
        // clamped to [0,1] — for a target AT pure white/black that offset
        // has nowhere to go on one side at all, so almost the ENTIRE
        // garment collapsed to one identical flat value there (measured on
        // a real photo: 85% of pixels became literal RGB 255,255,255 for
        // White, erasing every seam/fold/hood-shading).
        //
        // First fix attempt: slide the WHOLE natural shading shape so both
        // its extremes fit inside [0,1]. That over-corrected for Black —
        // this photo's own shading is centred on a HIGH mean (a mostly-
        // white garment, meanL~0.91), so fitting that entire shape below a
        // target of 0 dragged the *effective* target all the way up to a
        // medium GREY, nowhere near black (confirmed live: "Black" rendered
        // as washed-out grey).
        //
        // Correct fix: don't move the target away from what the customer
        // actually picked — just pull it back from the hard 0/1 edge by a
        // small MARGIN, and keep the plain additive formula. Most of the
        // garment (the near-mean pixels) still reads as the true target
        // colour; only the few pixels brighter than white-target+margin (or
        // darker than black-target-margin) clip to the true extreme, which
        // is correct — a real highlight SHOULD be able to hit pure white, a
        // real shadow crease SHOULD be able to hit pure black. Mid-tone
        // targets (navy, red, ...) already have plenty of headroom, so
        // MARGIN never changes them.
        var MARGIN = 0.05;
        var effectiveTargetL = Math.max(MARGIN, Math.min(1 - MARGIN, target[2]));

        for (var i2 = 0, p2 = 0; i2 < d.length; i2 += 4, p2++) {
          if (isBackdrop[p2]) { d[i2 + 3] = 0; continue; }
          if (keepWhite && keepWhite[p2]) { d[i2] = 255; d[i2 + 1] = 255; d[i2 + 2] = 255; d[i2 + 3] = 255; continue; }
          var finalL = Math.max(0, Math.min(1, effectiveTargetL + (Lsmooth[p2] - meanL)));
          var rgb = hslToRgb(target[0], target[1], finalL);
          d[i2] = rgb[0]; d[i2 + 1] = rgb[1]; d[i2 + 2] = rgb[2]; d[i2 + 3] = 255;
        }
        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL('image/png');
      });
    }
    return recolorCache[cacheKey];
  }

  // ─── Background removal — AI (customer-uploaded logos/photos) ─────────
  // Runs a small salient-object-detection model (U2NETP — the same model
  // rembg uses under the hood) entirely client-side via ONNX Runtime Web
  // (WASM), so it works on ANY backdrop, not just plain white — and never
  // uploads the image anywhere. Both onnxruntime-web (MIT, Microsoft) and
  // the U2NETP model (Apache-2.0, github.com/xuebinqin/U-2-Net) are
  // permissively licensed for commercial use; this integration is our own
  // code. Lazy-loaded (~10MB runtime + ~4.6MB model, cached after first
  // use) so pages that never touch this feature don't pay for it.
  var ortLoadPromise = null;
  function loadOrtRuntime() {
    if (ortLoadPromise) return ortLoadPromise;
    ortLoadPromise = new Promise(function (resolve, reject) {
      if (window.ort) { resolve(window.ort); return; }
      var script = document.createElement('script');
      script.src = root.dataset.ortJs;
      script.onload = function () { resolve(window.ort); };
      script.onerror = function () { reject(new Error('Could not load ONNX Runtime')); };
      document.head.appendChild(script);
    });
    return ortLoadPromise;
  }

  var aiSessionPromise = null;
  function loadBackgroundRemovalModel() {
    if (aiSessionPromise) return aiSessionPromise;
    aiSessionPromise = loadOrtRuntime().then(function (ort) {
      ort.env.wasm.wasmPaths = root.dataset.ortWasmBase;
      // Shopify storefront pages aren't cross-origin-isolated (no
      // COOP/COEP headers), so SharedArrayBuffer/real threads aren't
      // available anyway — pin to 1 to skip ORT's own detection probe.
      ort.env.wasm.numThreads = 1;
      return ort.InferenceSession.create(root.dataset.bgModelUrl, { executionProviders: ['wasm'] });
    });
    return aiSessionPromise;
  }

  // Model expects a fixed 320×320 RGB input, ImageNet-normalised
  // ((x/255 - mean) / std per channel), channels-first (CHW) layout —
  // confirmed against the actual u2netp.onnx graph, not guessed.
  var BG_MODEL_SIZE = 320;
  var BG_MODEL_MEAN = [0.485, 0.456, 0.406];
  var BG_MODEL_STD = [0.229, 0.224, 0.225];

  function removeBackgroundAI(dataUrl) {
    return Promise.all([loadImageEl(dataUrl), loadBackgroundRemovalModel()]).then(function (results) {
      var img = results[0], session = results[1];
      var size = BG_MODEL_SIZE;

      var pre = document.createElement('canvas');
      pre.width = size; pre.height = size;
      var preCtx = pre.getContext('2d');
      preCtx.drawImage(img, 0, 0, size, size);
      var src = preCtx.getImageData(0, 0, size, size).data;

      var chw = new Float32Array(3 * size * size);
      var plane = size * size;
      for (var i = 0; i < plane; i++) {
        chw[i] = (src[i * 4] / 255 - BG_MODEL_MEAN[0]) / BG_MODEL_STD[0];
        chw[plane + i] = (src[i * 4 + 1] / 255 - BG_MODEL_MEAN[1]) / BG_MODEL_STD[1];
        chw[2 * plane + i] = (src[i * 4 + 2] / 255 - BG_MODEL_MEAN[2]) / BG_MODEL_STD[2];
      }

      var ort = window.ort;
      var inputTensor = new ort.Tensor('float32', chw, [1, 3, size, size]);
      return session.run({ 'input.1': inputTensor }).then(function (outputs) {
        // First output is the model's main fused saliency map (values
        // already 0..1 — sigmoid is baked into the graph); the other 6
        // outputs are intermediate side-outputs used only during training.
        var mask = outputs[session.outputNames[0]].data;

        var maskCanvas = document.createElement('canvas');
        maskCanvas.width = size; maskCanvas.height = size;
        var maskCtx = maskCanvas.getContext('2d');
        var maskImg = maskCtx.createImageData(size, size);
        for (var m = 0; m < plane; m++) {
          var a = Math.max(0, Math.min(255, Math.round(mask[m] * 255)));
          maskImg.data[m * 4] = 255; maskImg.data[m * 4 + 1] = 255; maskImg.data[m * 4 + 2] = 255;
          maskImg.data[m * 4 + 3] = a;
        }
        maskCtx.putImageData(maskImg, 0, 0);

        // Composite at the ORIGINAL resolution (not the 320×320 the model
        // sees) — draw the full-quality source image, then punch the
        // (smoothly upscaled, browser-interpolated) mask's alpha into it.
        var out = document.createElement('canvas');
        out.width = img.naturalWidth; out.height = img.naturalHeight;
        var outCtx = out.getContext('2d');
        outCtx.drawImage(img, 0, 0);
        outCtx.globalCompositeOperation = 'destination-in';
        outCtx.drawImage(maskCanvas, 0, 0, img.naturalWidth, img.naturalHeight);
        outCtx.globalCompositeOperation = 'source-over';
        return out.toDataURL('image/png');
      });
    });
  }

  // ─── Background removal — fallback (plain white/light backdrops) ──────
  // Used only if the AI model fails to load (blocked script, unsupported
  // browser, offline first use, etc.) — a simpler, instant, no-download
  // flood-fill that still handles the single most common case: a logo
  // exported on a plain white canvas.
  function removeWhiteBackground(dataUrl) {
    return loadImageEl(dataUrl).then(function (img) {
      var W = img.naturalWidth, H = img.naturalHeight;
      var canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      var imageData = ctx.getImageData(0, 0, W, H);
      var d = imageData.data;
      // Slightly more lenient than the recolour pipeline's studio-photo
      // threshold (250), since logo exports often have soft anti-aliased
      // edges rather than a perfectly clean white.
      var LUM_THRESHOLD = 244;
      // Low-saturation only, so a bright but vivid colour that's actually
      // part of the logo (e.g. light yellow) doesn't get punched out too.
      var SAT_THRESHOLD = 20;

      function isBackdropPixel(idx) {
        var p = idx * 4;
        if (d[p + 3] === 0) return true; // already transparent
        var r = d[p], g = d[p + 1], b = d[p + 2];
        var lum = (r + g + b) / 3;
        var sat = Math.max(r, g, b) - Math.min(r, g, b);
        return lum >= LUM_THRESHOLD && sat <= SAT_THRESHOLD;
      }

      // Flood-fill from the image edges only — a light pixel has to be
      // *connected* to the border to count as backdrop, so pale highlights
      // inside the logo itself are left alone.
      var isBackdrop = new Uint8Array(W * H);
      var visited = new Uint8Array(W * H);
      var stack = [];
      for (var x = 0; x < W; x++) { stack.push(x); stack.push((H - 1) * W + x); }
      for (var y = 0; y < H; y++) { stack.push(y * W); stack.push(y * W + W - 1); }
      while (stack.length) {
        var idx = stack.pop();
        if (visited[idx]) continue;
        visited[idx] = 1;
        if (!isBackdropPixel(idx)) continue;
        isBackdrop[idx] = 1;
        var px = idx % W, py = (idx / W) | 0;
        if (px > 0) stack.push(idx - 1);
        if (px < W - 1) stack.push(idx + 1);
        if (py > 0) stack.push(idx - W);
        if (py < H - 1) stack.push(idx + W);
      }

      for (var p2 = 0; p2 < W * H; p2++) {
        if (isBackdrop[p2]) d[p2 * 4 + 3] = 0;
      }
      ctx.putImageData(imageData, 0, 0);
      return canvas.toDataURL('image/png');
    });
  }

  // ─── Unified garment → image src ───────────────────────────────────────
  function isPhotoGarment(g) { return !!(g && g.photoViews); }
  function isVectorGarment(g) { return !!(g && g.views); }

  function getGarmentPreviewSrc(garment, colorHex, view) {
    if (isPhotoGarment(garment)) {
      // garment.keepZoneWhite (Mug/Artmug only — see garments-data.js):
      // the print zone marked on the template stays neutral white
      // regardless of the chosen mug colour (a two-tone template — grey
      // rim band + white cup surface), instead of getting tinted like the
      // rest of the artwork. Bottle's template has no such second tone
      // (it's meant to recolour edge-to-edge), so it doesn't set this.
      var keepWhiteZone = garment.keepZoneWhite ? garment.photoViews[view].zone : null;
      // Photo garments used to pass blurRadius=4 here ("smooth photographed
      // -fabric texture noise") — tested side by side against real photos
      // (plain white, a saturated red, and one of the sharpened/upscaled
      // photos) and in every case blur=4 just visibly softened seams/collar
      // -rib/hood texture with no corresponding benefit (no blotchy noise
      // appeared at blur=0 to justify it). 0 now matches flatArtwork's own
      // value — kept as a real function parameter (not deleted) in case a
      // genuinely noisy future source photo needs it dialled back up.
      return getRecoloredSrc(resolveAsset(garment.photoViews[view].image), colorHex, 0, keepWhiteZone);
    }
    return Promise.resolve(svgToDataUri(buildVectorGarmentSvg(garment, view)));
  }

  function activeView() {
    return state.view;
  }
  function activeZoneDef() {
    var g = state.garment;
    if (isPhotoGarment(g)) return g.photoViews[state.view].zone;
    if (isVectorGarment(g)) return (g.views[state.view] || g.views[viewKeys()[0]]).zone;
    return { xPct: 30, yPct: 30, wPct: 40, hPct: 40 };
  }
  function viewKeys() {
    var g = state.garment;
    if (isPhotoGarment(g)) return Object.keys(g.photoViews);
    if (isVectorGarment(g)) return Object.keys(g.views);
    return ['front'];
  }
  function hasMultipleViews() {
    return viewKeys().length > 1;
  }
  function viewLabel(garment, key) {
    if (isPhotoGarment(garment)) return garment.photoViews[key].label || key;
    if (isVectorGarment(garment)) return (garment.views[key] && garment.views[key].label) || key;
    return key;
  }

  function zoneFor(view) {
    if (!state.viewZones[view]) {
      var def = activeZoneDef();
      state.viewZones[view] = { leftPct: def.xPct, topPct: def.yPct, wPct: def.wPct, hPct: def.hPct };
    }
    return state.viewZones[view];
  }

  // ─── Rail navigation ────────────────────────────────────────────────────
  // Garment / Colour / Your Logo / Size toggle exclusively amongst themselves.
  // Passing null (or clicking the already-open rail button again) closes the
  // panel area entirely, leaving just the product info + canvas.
  var activePanelName = null;
  function setActivePanel(name) {
    activePanelName = name;
    document.querySelectorAll('[data-panel]').forEach(function (b) {
      b.classList.toggle('is-active', !!name && b.getAttribute('data-panel') === name);
    });
    els.panelArea.hidden = !name;
    els.panelArea.querySelectorAll('[data-panel-content]').forEach(function (p) {
      p.hidden = p.getAttribute('data-panel-content') !== name;
    });
  }

  // ─── Mobile: jump-to-section (post-questionnaire) ──────────────────────
  // On a phone the rail/canvas/tool-panel/product-info are one long
  // scrolling page instead of desktop's side-by-side columns — tapping a
  // rail button scrolls its panel into view instead of leaving the customer
  // to hunt for it themselves (the rail stays sticky at the top specifically
  // so this works from anywhere, not just from the top of the page — see
  // design-tool.css). This is what's left available once the guided
  // first-run questionnaire below has finished (or for a garment it doesn't
  // apply to at all).
  function isMobileViewport() {
    return window.matchMedia('(max-width: 900px)').matches;
  }
  function scrollMobileTo(el) {
    if (!el || !isMobileViewport()) return;
    requestAnimationFrame(function () { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
  }

  document.querySelectorAll('[data-panel]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var name = btn.getAttribute('data-panel');
      var opening = activePanelName !== name;
      setActivePanel(activePanelName === name ? null : name);
      if (!opening) return;
      scrollMobileTo(els.panelArea);
      // One-tap upload: opening the Logo panel goes straight to the file
      // picker instead of making the customer tap "Carregar imagem(ns)"
      // again once the panel's already open in front of them. The panel
      // still opens normally either way, so cancelling the file picker
      // just leaves them looking at its own upload button as a fallback.
      if (name === 'logo') els.logoInput.click();
    });
  });
  setActivePanel('garment');

  // ─── Mobile: guided first-run questionnaire ────────────────────────────
  // A full-screen, one-question-at-a-time flow shown the moment the editor
  // opens on a phone: Product → Quantity → Colour → Size → Technique
  // (skipping whichever don't apply to the chosen garment), each answer
  // immediately advancing to the next question — instead of leaving a
  // first-time customer to find all of those on their own via the rail,
  // which is what the "jump-to-section" behaviour above is for once this is
  // done. Reuses the SAME garment/colour/size/technique panels the normal
  // editor uses (relocated in here via appendChild, then put back once the
  // questionnaire ends) rather than duplicating any of that picker logic —
  // only the quantity step has no existing standalone element to reuse (it
  // normally lives inline inside the Informação do Produto card, which is
  // rebuilt wholesale on every render and so isn't safe to relocate) and
  // gets its own minimal dedicated input instead.
  var wizardActive = false;
  function wizardPanelFor(name) {
    return document.querySelector('.dt-panel[data-panel-content="' + name + '"]');
  }
  function wizardStepLabel(text) {
    if (els.wizardStep) els.wizardStep.textContent = text;
  }
  // Keyed by t() key, not by reading the reparented panel's own title text
  // — that title element holds BOTH the PT and EN <span>s at once (only one
  // shown via [hidden]), and .textContent ignores the hidden attribute,
  // returning both languages glued together.
  var WIZARD_STEP_LABEL_KEY = { garment: 'wizardStepGarment', qty: 'howManyWouldYouLike', colour: 'wizardStepColour', size: 'size', technique: 'technique' };
  function showWizardStep(name) {
    if (els.wizardQty) els.wizardQty.hidden = true;
    ['garment', 'colour', 'size', 'technique'].forEach(function (n) {
      var el = wizardPanelFor(n);
      if (el) el.hidden = true;
    });
    wizardStepLabel(t(WIZARD_STEP_LABEL_KEY[name] || name));
    if (name === 'qty') {
      els.wizardQty.hidden = false;
      els.wizardQtyInput.value = state.qtyTotal || '';
      els.wizardQtyInput.focus();
      return;
    }
    var el = wizardPanelFor(name);
    if (!el) { endMobileWizard(); return; } // shouldn't happen, but never strand the customer on a blank screen
    els.wizardBody.appendChild(el);
    el.hidden = false;
  }
  function startMobileWizard() {
    if (!isMobileViewport()) return;
    wizardActive = true;
    els.wizard.hidden = false;
    showWizardStep('garment');
  }
  function endMobileWizard() {
    if (!wizardActive) return;
    wizardActive = false;
    els.wizard.hidden = true;
    // Put every relocated panel back where setActivePanel expects to find
    // it — order among panel-area's children doesn't matter, only one is
    // ever shown at a time (see setActivePanel).
    ['garment', 'colour', 'size', 'technique'].forEach(function (n) {
      var el = wizardPanelFor(n);
      if (el && el.parentNode !== els.panelArea) els.panelArea.appendChild(el);
    });
    setActivePanel(null);
  }
  function wizardNextAfterGarment() {
    if (!wizardActive) return;
    if (!matchedProduct(state.garment)) { endMobileWizard(); return; } // no qty/colour/size/technique to ask for an unlinked garment
    showWizardStep('qty');
  }
  function wizardNextAfterQty() {
    if (!wizardActive) return;
    var g = state.garment;
    if (g.showColor === false) { wizardNextAfterColour(); return; }
    showWizardStep('colour');
  }
  function wizardNextAfterColour() {
    if (!wizardActive) return;
    var g = state.garment;
    if (!(allSizesForGarment(g).length > 0)) { wizardNextAfterSize(); return; }
    showWizardStep('size');
  }
  function wizardNextAfterSize() {
    if (!wizardActive) return;
    var g = state.garment;
    if (g.showTechnique === false) { endMobileWizard(); return; }
    showWizardStep('technique');
  }
  function wizardNextAfterTechnique() {
    endMobileWizard();
  }
  if (els.wizardSkip) els.wizardSkip.addEventListener('click', endMobileWizard);
  if (els.wizardQtyNext) {
    els.wizardQtyNext.addEventListener('click', function () {
      var v = Math.max(0, parseInt(els.wizardQtyInput.value, 10) || 0);
      if (!v) { els.wizardQtyInput.focus(); return; }
      state.qtyTotal = v;
      renderInfoPanel();
      renderColorPicker();
      renderSizePicker();
      updateAddToCartState();
      pushHistory();
      wizardNextAfterQty();
    });
  }

  els.railToggle.addEventListener('click', function () {
    els.rail.classList.toggle('is-collapsed');
  });

  // ─── Product info column ────────────────────────────────────────────────
  // Persistent alongside whichever tool panel is active — shown by default,
  // closed via its own X or the info icon in the toolbar (both just toggle
  // visibility of the whole left column).
  function setInfoVisible(visible) {
    els.leftCol.hidden = !visible;
    els.infoToggle.classList.toggle('is-active', visible);
  }
  els.infoToggle.addEventListener('click', function () {
    setInfoVisible(els.leftCol.hidden);
  });
  els.infoClose.addEventListener('click', function () {
    setInfoVisible(false);
  });
  setInfoVisible(true);

  els.railDownload.addEventListener('click', function () {
    els.downloadBtn.click();
  });

  // Edit/Preview mode toggle was removed from the header to make room for the
  // zoom/pan controls (moved up from the left column — see design-tool.liquid);
  // els.modeToggle is now always null. The is-preview-mode CSS it drove
  // (design-tool.css) is harmless dead weight, kept in case this comes back.

  // ─── Undo / redo ────────────────────────────────────────────────────────
  // A simple snapshot stack over the parts of state a customer actually
  // changes (garment, colour, size, technique, logo, view/placement) — pushed
  // after each committed action, restored wholesale on undo/redo.
  var history = { stack: [], index: -1 };

  function snapshotState() {
    return JSON.stringify({
      garmentId: state.garment.id,
      garmentConfirmed: state.garmentConfirmed,
      colorName: state.color.name,
      size: state.size,
      sizeQty: state.sizeQty,
      qtyTotal: state.qtyTotal,
      colorQty: state.colorQty,
      technique: state.technique,
      viewLayers: state.viewLayers,
      activeLayerId: state.activeLayerId,
      view: state.view,
      viewZones: state.viewZones,
    });
  }

  function pushHistory() {
    var snap = snapshotState();
    if (history.index >= 0 && history.stack[history.index] === snap) return; // no real change
    history.stack = history.stack.slice(0, history.index + 1);
    history.stack.push(snap);
    history.index = history.stack.length - 1;
    updateUndoRedoButtons();
  }

  function applySnapshot(snap) {
    var g = GARMENTS.find(function (g) { return g.id === snap.garmentId; }) || state.garment;
    state.garment = g;
    state.garmentConfirmed = !!snap.garmentConfirmed;
    state.color = colorsForGarment(g).filter(function (c) { return c.name === snap.colorName; })[0] || colorsForGarment(g)[0];
    state.size = snap.size;
    state.sizeQty = snap.sizeQty || {};
    state.qtyTotal = snap.qtyTotal != null ? snap.qtyTotal : null;
    state.colorQty = snap.colorQty || {};
    state.technique = snap.technique;
    state.viewLayers = snap.viewLayers || {};
    state.activeLayerId = snap.activeLayerId || null;
    state.view = snap.view;
    state.viewZones = snap.viewZones || {};

    var railGarmentIcon = document.getElementById('dt-rail-garment-icon');
    if (railGarmentIcon) setIconInto(railGarmentIcon, g.icon);
    els.techniqueRow.querySelectorAll('[data-technique]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-technique') === state.technique);
    });
    els.propTechnique.value = state.technique;

    renderGarmentPicker();
    renderColorPicker();
    renderSizePicker();
    renderLayersPanel();
    renderPreview();
    renderInfoPanel();
    updateWhatsappLink();
    updateGarmentGate();
  }

  function updateUndoRedoButtons() {
    els.undoBtn.disabled = history.index <= 0;
    els.redoBtn.disabled = history.index >= history.stack.length - 1;
  }

  els.undoBtn.addEventListener('click', function () {
    if (history.index <= 0) return;
    history.index--;
    applySnapshot(JSON.parse(history.stack[history.index]));
    updateUndoRedoButtons();
  });
  els.redoBtn.addEventListener('click', function () {
    if (history.index >= history.stack.length - 1) return;
    history.index++;
    applySnapshot(JSON.parse(history.stack[history.index]));
    updateUndoRedoButtons();
  });

  // ─── Controls ──────────────────────────────────────────────────────────
  // Finalises picking a specific garment (called either straight from a
  // single-garment category tile, or from a category's sub-picker).
  function selectGarment(g) {
    state.garment = g;
    state.garmentPickerCategory = null; // reset so the picker opens on categories next time
    state.garmentConfirmed = true;
    state.color = colorsForGarment(g)[0] || GARMENT_COLORS[0];
    state.size = defaultSizeFor(g);
    state.sizeQty = defaultSizeQtyFor(g);
    state.qtyTotal = null;
    state.colorQty = {};
    state.view = isPhotoGarment(g) ? Object.keys(g.photoViews)[0] : Object.keys(g.views)[0];
    state.viewZones = {};
    state.viewLayers = {};
    state.activeLayerId = null;
    var railGarmentIcon = document.getElementById('dt-rail-garment-icon');
    if (railGarmentIcon) setIconInto(railGarmentIcon, g.icon);
    renderGarmentPicker();
    renderColorPicker();
    renderSizePicker();
    renderPreview();
    renderInfoPanel();
    updateWhatsappLink();
    updateGarmentGate();
    wizardNextAfterGarment(); // no-op on desktop / outside the questionnaire — see its own comment
    pushHistory();
  }

  // Renders either the top-level category grid, or (once a multi-garment
  // category like "cap" has been tapped) that category's sub-grid of
  // individual garments — see GARMENT_CATEGORIES above.
  function renderGarmentPicker() {
    els.garmentGrid.innerHTML = '';
    var openCategory = state.garmentPickerCategory
      ? GARMENT_CATEGORIES.find(function (c) { return c.key === state.garmentPickerCategory; })
      : null;
    els.garmentBack.hidden = !openCategory;

    if (openCategory) {
      openCategory.items.forEach(function (g) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dt-garment-btn' + (g.id === state.garment.id ? ' is-selected' : '');
        btn.dataset.garmentId = g.id;
        btn.innerHTML = iconHtml(g.icon) + '<span>' + (g.variantLabel || g.name) + '</span>';
        btn.addEventListener('click', function () { selectGarment(g); });
        els.garmentGrid.appendChild(btn);
      });
    } else {
      GARMENT_CATEGORIES.forEach(function (cat) {
        var isSingle = cat.items.length === 1;
        var isSelected = !isSingle && cat.items.some(function (g) { return g.id === state.garment.id; })
          || (isSingle && cat.items[0].id === state.garment.id);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dt-garment-btn' + (isSelected ? ' is-selected' : '');
        btn.dataset.categoryKey = cat.key;
        if (isSingle) btn.dataset.single = 'true';
        btn.innerHTML = iconHtml(cat.icon) + '<span>' + cat.name + '</span>';
        btn.addEventListener('click', function () {
          if (isSingle) {
            selectGarment(cat.items[0]);
          } else {
            state.garmentPickerCategory = cat.key;
            renderGarmentPicker();
          }
        });
        els.garmentGrid.appendChild(btn);
      });
    }

    // Lets the "Painel do Editor" Chrome extension (if installed) know the
    // picker just (re)rendered and which screen it's showing, so it can
    // overlay its own edit/add controls without guessing from the DOM —
    // does nothing at all without that extension listening.
    window.DT_PICKER_STATE = { openCategoryKey: openCategory ? openCategory.key : null };
    window.dispatchEvent(new CustomEvent('dt:garmentpicker'));
  }

  els.garmentBack.addEventListener('click', function () {
    state.garmentPickerCategory = null;
    renderGarmentPicker();
  });

  // Independent size quantity list — shown for any garment with a matched
  // real product that actually has a Size option (a size-less product like a
  // mug just hides this tab, since colour quantities alone already cover its
  // whole order — see canAddToCart). Sizes with no available variant at all
  // are shown but disabled, labelled "No stock". This total is independent
  // from the colour quantities (not paired per unit — see canAddToCart);
  // both just need to separately sum to state.qtyTotal.
  function renderSizeQtyList(garment) {
    var sizes = allSizesForGarment(garment);
    els.railSizeBtn.hidden = !(sizes && sizes.length);
    if (!sizes || !sizes.length) return;

    var target = state.qtyTotal;
    var distributed = totalSizeQty(garment);

    // Once 2+ colours are chosen, the whole order can only have ONE size —
    // see addToCartBlockReason's onlyOneSizeWithMultipleColours check, which
    // is the real gate; this just steers the customer away from creating
    // that block in the first place by locking the other size inputs once
    // one is already picked. Never clears an existing value on its own — if
    // they already had 2 sizes filled in before picking a 2nd colour, the
    // block-reason message (not a silent reset) tells them to fix it.
    var singleSizeMode = garment.showColor !== false && colorQtyEntries(garment).length > 1;
    var chosenSize = singleSizeMode
      ? sizes.map(function (s) { return s.size; }).find(function (sz) { return (state.sizeQty[sz] || 0) > 0; })
      : null;

    els.sizeRow.innerHTML = '';
    var status = document.createElement('p');
    status.className = 'dt-hint ' + (target && distributed === target ? 'is-complete' : 'is-error');
    status.textContent = target
      ? t('distributedBySize', { done: distributed, total: target })
      : t('setQtyFirst');
    els.sizeRow.appendChild(status);
    if (singleSizeMode) {
      var note = document.createElement('p');
      note.className = 'dt-hint';
      note.textContent = t('singleSizeWithMultipleColoursHint');
      els.sizeRow.appendChild(note);
    }

    sizes.forEach(function (s) {
      var row = document.createElement('div');
      row.className = 'dt-size-qty-row' + (!s.available ? ' is-out-of-stock' : '');
      var lockedOut = singleSizeMode && chosenSize && s.size !== chosenSize;
      row.innerHTML =
        '<span class="dt-size-qty-label">' + s.size + '</span>' +
        (s.available
          ? '<input type="number" min="0" max="999" class="dt-size-qty-row__input" placeholder="0">'
          : '<span class="dt-hint">' + t('noStock') + '</span>');
      if (s.available) {
        var input = row.querySelector('.dt-size-qty-row__input');
        input.value = state.sizeQty[s.size] || '';
        input.disabled = !target || lockedOut;
        input.addEventListener('change', function () {
          var v = Math.max(0, parseInt(input.value, 10) || 0);
          if (v) state.sizeQty[s.size] = v; else delete state.sizeQty[s.size];
          renderInfoPanel();
          updateAddToCartState();
          if (target && totalSizeQty(garment) === target) wizardNextAfterSize();
          pushHistory();
          renderSizeQtyList(garment); // re-lock/unlock the other size inputs
        });
      }
      els.sizeRow.appendChild(row);
    });
  }

  function renderSizePicker() {
    // Owner override (set via the Chrome extension's per-garment toggles,
    // independent of whatever the real matched product actually has) —
    // checked first since it should win regardless of real product data.
    if (state.garment.showSize === false) {
      els.railSizeBtn.hidden = true;
      els.sizeRow.innerHTML = '';
      return;
    }
    var product = matchedProduct(state.garment);
    if (product) {
      renderSizeQtyList(state.garment);
      return;
    }

    // Fallback: old hand-typed GARMENT_PRODUCTS table (garment has no matched real product yet).
    var railLabel = els.railSizeBtn.querySelector('.dt-rail__label');
    if (railLabel) railLabel.textContent = t('size');
    var legacyProduct = GARMENT_PRODUCTS[state.garment.id];
    var show = !!(legacyProduct && legacyProduct.hasSizes);
    els.railSizeBtn.hidden = !show;
    if (!show) return;
    els.sizeRow.innerHTML = '';
    Object.keys(legacyProduct.sizes).forEach(function (size) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dt-size-btn' + (size === state.size ? ' is-selected' : '');
      btn.textContent = size;
      btn.addEventListener('click', function () {
        state.size = size;
        renderSizePicker();
        pushHistory();
      });
      els.sizeRow.appendChild(btn);
    });
  }

  function renderColorPicker() {
    // Same owner-override pattern as renderSizePicker above, plus the
    // Technique rail button — bundled in here since every call site
    // already calls this and renderSizePicker as a pair, and Technique has
    // no picker function of its own to hold this check instead.
    els.railTechniqueBtn.hidden = state.garment.showTechnique === false;
    if (state.garment.showColor === false) {
      els.railColorBtn.hidden = true;
      els.colorRow.innerHTML = '';
      return;
    }
    els.railColorBtn.hidden = false;
    els.colorRow.innerHTML = '';
    var product = matchedProduct(state.garment);
    var colors = colorsForGarment(state.garment);

    function previewColor(c) {
      state.color = c;
      renderColorPicker();
      renderPreview();
      renderInfoPanel();
      updateWhatsappLink();
      pushHistory();
    }

    if (product) {
      // Each colour gets its own quantity — an independent breakdown (not
      // paired to specific sizes, see canAddToCart) — plus a swatch to switch
      // which colour is currently being previewed/designed on. Quantity
      // inputs stay disabled until a total is set in Product Information —
      // otherwise a customer could fill these in before the total exists,
      // which can never add up to anything.
      els.colorRow.className = 'dt-color-qty-list';
      var qtyTarget = state.qtyTotal;
      var qtyStatus = document.createElement('p');
      qtyStatus.className = 'dt-hint ' + (qtyTarget && totalColorQty(state.garment) === qtyTarget ? 'is-complete' : 'is-error');
      qtyStatus.textContent = qtyTarget
        ? t('distributedByColour', { done: totalColorQty(state.garment), total: qtyTarget })
        : t('setQtyFirst');
      els.colorRow.appendChild(qtyStatus);
      colors.forEach(function (c) {
        var row = document.createElement('div');
        row.className = 'dt-color-qty-row' + (c.name === state.color.name ? ' is-previewing' : '');
        var previewLabel = t('previewColour', { name: c.name });
        row.innerHTML =
          '<button type="button" class="dt-color-qty-row__swatch" style="background:' + c.hex + '" title="' + previewLabel + '" aria-label="' + previewLabel + '"></button>' +
          '<span class="dt-color-qty-row__label">' + c.name + '</span>' +
          '<input type="number" min="0" max="999" class="dt-color-qty-row__input" placeholder="0">';
        row.querySelector('.dt-color-qty-row__swatch').addEventListener('click', function () { previewColor(c); });
        var input = row.querySelector('.dt-color-qty-row__input');
        input.value = state.colorQty[c.name] || '';
        input.disabled = !qtyTarget;
        input.addEventListener('change', function () {
          var v = Math.max(0, parseInt(input.value, 10) || 0);
          if (v) state.colorQty[c.name] = v; else delete state.colorQty[c.name];
          renderInfoPanel();
          updateAddToCartState();
          if (qtyTarget && totalColorQty(state.garment) === qtyTarget) wizardNextAfterColour();
          pushHistory();
          renderSizeQtyList(state.garment); // 2nd colour locks size to just one (see there)
        });
        els.colorRow.appendChild(row);
      });
    } else {
      els.colorRow.className = 'dt-color-row';
      colors.forEach(function (c) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dt-color-swatch' + (c.name === state.color.name ? ' is-selected' : '');
        btn.style.background = c.hex;
        btn.title = c.name;
        btn.addEventListener('click', function () { previewColor(c); });
        els.colorRow.appendChild(btn);
      });
    }
    els.colorName.textContent = state.color.name;
  }

  els.techniqueRow.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-technique]');
    if (!btn) return;
    state.technique = btn.getAttribute('data-technique');
    els.propTechnique.value = state.technique;
    els.techniqueRow.querySelectorAll('[data-technique]').forEach(function (b) { b.classList.remove('is-active'); });
    btn.classList.add('is-active');
    renderInfoPanel();
    updateAddToCartState();
    wizardNextAfterTechnique();
    pushHistory();
  });

  // Any technique beyond Embroidery/Print (a product tagged "Technique" in
  // the admin — see design-tool.liquid) gets its own button appended here
  // automatically, since those two are the only ones with hand-translated
  // PT/EN labels baked into the theme's own HTML — a new one just shows
  // its product title as-is, in whichever language the owner typed it.
  (function renderExtraTechniqueButtons() {
    var known = { embroidery: true, print: true };
    Object.keys(FEE_PRODUCTS).forEach(function (name) {
      if (known[name.toLowerCase()]) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dt-seg__btn';
      btn.setAttribute('data-technique', name);
      btn.textContent = name;
      els.techniqueRow.appendChild(btn);
    });
  })();

  // ─── Product info panel ─────────────────────────────────────────────────
  // Shows the real matched Shopify product (title, photo, price) plus the
  // customisation fee and the customer's current selections — or, for
  // garments not yet linked to a real "Blanks" product, just the selections.
  function renderInfoPanel() {
    if (!els.infoContent) return;
    // Before the customer actively picks a garment tile, state.garment is
    // just the array's default (e.g. the first t-shirt) — showing its price/
    // info here would look like that garment is already chosen. Keep this
    // panel a plain "pick something" placeholder until garmentConfirmed.
    if (!state.garmentConfirmed) {
      els.infoContent.innerHTML = '<p class="dt-hint">' + t('chooseGarmentToStart') + '</p>';
      updateBottomBarTotal(null);
      return;
    }
    var g = state.garment;
    var product = matchedProduct(g);

    if (!product) {
      els.infoContent.innerHTML =
        '<p class="dt-hint">' + t('notLinkedYet') + '</p>' +
        '<div class="dt-info-summary">' +
          '<div><span>' + t('garment') + '</span><strong>' + g.name + '</strong></div>' +
          '<div><span>' + t('colour') + '</span><strong>' + state.color.name + '</strong></div>' +
          (state.size ? '<div><span>' + t('size') + '</span><strong>' + state.size + '</strong></div>' : '') +
          (g.showTechnique !== false && state.technique ? '<div><span>' + t('technique') + '</span><strong>' + techniqueLabel(state.technique) + '</strong></div>' : '') +
        '</div>';
      updateBottomBarTotal(null);
      return;
    }

    var target = state.qtyTotal;
    var colorEntries = colorQtyEntries(g) || [];
    var sizeEntries = sizeQtyEntries(g) || [];
    var sizesExist = allSizesForGarment(g).length > 0;
    var colorTotal = totalColorQty(g);
    var sizeTotal = totalSizeQty(g);
    var basePrice = product.variants[0].price;
    var hasElements = totalLayerCount() > 0;
    // A customisation fee only ever applies once a technique is actually
    // chosen — no technique pre-selects any more (the owner didn't want
    // Embroidery silently defaulting), so "has a design but no technique
    // yet" must show a prompt, never a made-up price. Bordado (Embroidery)
    // itself never has a fixed online price at all any more — it's quote
    // -only over WhatsApp (see sendEmbroideryQuoteViaWhatsapp) — so it shows
    // "Sob consulta" instead of an amount, and never adds to lineTotal.
    var needsTechnique = hasElements && g.showTechnique !== false;
    var isEmbroideryQuote = needsTechnique && state.technique === 'Embroidery';
    var isPrintPerView = needsTechnique && state.technique === 'Print';
    // Print no longer has ONE combined fee — it's priced per view (see
    // computePrintFeeLines, used at actual checkout). This panel can only
    // show the CURRENTLY DISPLAYED view's own price as a live estimate
    // (currentViewPrintEstimate is synchronous, checkout's real calculation
    // isn't) — multiWarning appears whenever there's more than one
    // view with a design, making clear this number isn't the full total.
    var printEstimate = isPrintPerView ? currentViewPrintEstimate() : null;
    var printHasMultipleViews = isPrintPerView && viewKeys().filter(function (k) { return layersForView(k).length > 0; }).length > 1;
    var feeCents = (needsTechnique && state.technique && !isEmbroideryQuote && !isPrintPerView) ? customizationFeeCentsFor(state.technique) : 0;
    // Print's contribution to the running total only ever reflects what's
    // known for certain right now (the current view alone) — never the
    // guessed total for other, not-currently-displayed views.
    if (isPrintPerView && printEstimate) feeCents = printEstimate.cents;
    var lineTotal = target ? (basePrice + feeCents) * target : 0;
    var colorLine = colorEntries.length ? colorEntries.map(function (e) { return e.name + ' × ' + e.qty; }).join(', ') : null;
    var sizeLine = sizeEntries.length ? sizeEntries.map(function (e) { return e.size + ' × ' + e.qty; }).join(', ') : null;

    var colorOk = target && colorTotal === target;
    var sizeOk = !sizesExist || (target && sizeTotal === target);

    els.infoContent.innerHTML =
      '<div class="dt-info-product">' +
        (product.image ? '<img src="' + product.image + '" alt="' + product.title + '">' : '') +
        '<div>' +
          '<a href="' + product.url + '" target="_blank" rel="noopener" class="dt-info-product__title">' + product.title + '</a>' +
          '<span class="dt-hint">' + t('inStock') + '</span>' +
          (product.description ? '<p class="dt-info-product__desc">' + product.description + '</p>' : '') +
        '</div>' +
      '</div>' +
      '<div class="dt-qty-total-row">' +
        '<label for="dt-qty-total-input">' + t('howManyWouldYouLike') + '</label>' +
        '<input type="number" min="1" max="999" id="dt-qty-total-input" value="' + (target || '') + '" placeholder="e.g. 5">' +
      '</div>' +
      (target
        ? (
            '<p class="dt-hint ' + (colorOk ? 'is-complete' : 'is-error') + '">' + t('distributedByColourCheck', { done: colorTotal, total: target, check: colorOk ? ' ✓' : '' }) + '</p>' +
            (sizesExist ? '<p class="dt-hint ' + (sizeOk ? 'is-complete' : 'is-error') + '">' + t('distributedBySizeCheck', { done: sizeTotal, total: target, check: sizeOk ? ' ✓' : '' }) + '</p>' : '')
          )
        : '<p class="dt-hint">' + t('enterQtyThenDistribute') + '</p>') +
      '<div class="dt-info-price">' +
        '<div><span>' + t('blankGarment') + (target > 1 ? t('each') : '') + '</span><span>' + formatMoney(basePrice) + '</span></div>' +
        (!needsTechnique
          ? (hasElements ? '' : '<div><span>' + t('customisation') + '</span><span class="dt-hint">' + t('addDesignToInclude') + '</span></div>')
          : !state.technique
            ? '<div><span>' + t('customisation') + '</span><span class="dt-hint">' + t('chooseTechniqueForDesign') + '</span></div>'
            : isEmbroideryQuote
              ? '<div><span>' + t('customisation') + ' (' + techniqueLabel(state.technique) + ')</span><span class="dt-hint">' + t('quoteOnly') + '</span></div>'
              : isPrintPerView
                ? (printEstimate
                    ? '<div><span>' + t('customisation') + ' (' + techniqueLabel(state.technique) + ')</span><span>+' + formatMoney(printEstimate.cents) + ' <span class="dt-hint">' + t('printThisView', { cm: Math.round(printEstimate.cm) }) + '</span></span></div>' +
                      (printHasMultipleViews ? '<div class="dt-hint">' + t('printPerViewNote') + '</div>' : '')
                    : '<div><span>' + t('customisation') + ' (' + techniqueLabel(state.technique) + ')</span><span class="dt-hint">' + t('measuringSize') + '</span></div>')
                : '<div><span>' + t('customisation') + ' (' + techniqueLabel(state.technique) + (target > 1 ? t('eachShort') : '') + ')</span><span>+' + formatMoney(feeCents) + '</span></div>') +
        (target > 1 ? '<div><span>' + t('quantity') + '</span><span>' + target + '</span></div>' : '') +
        '<div class="dt-info-price__total"><span>' + t('total') + '</span><span>' + formatMoney(lineTotal) + '</span></div>' +
      '</div>' +
      '<div class="dt-info-summary">' +
        (colorLine ? '<div><span>' + t('colours') + '</span><strong>' + colorLine + '</strong></div>' : '') +
        (sizeLine ? '<div><span>' + t('sizes') + '</span><strong>' + sizeLine + '</strong></div>' : '') +
        (g.showTechnique !== false && state.technique ? '<div><span>' + t('technique') + '</span><strong>' + techniqueLabel(state.technique) + '</strong></div>' : '') +
      '</div>';
    updateBottomBarTotal(lineTotal);

    var qtyInput = document.getElementById('dt-qty-total-input');
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var v = Math.max(0, parseInt(qtyInput.value, 10) || 0);
        state.qtyTotal = v > 0 ? v : null;
        renderInfoPanel();
        renderColorPicker();
        renderSizePicker();
        updateAddToCartState();
        if (v > 0) mobileOnboardingReachedQty();
        pushHistory();
      });
    }
  }

  // ─── Preview (unified: vector + photo) ─────────────────────────────────
  function renderViewTabs() {
    var keys = viewKeys();
    var g = state.garment;
    els.viewTabs.innerHTML = keys.map(function (key) {
      return '<button type="button" class="' + (key === state.view ? 'is-active' : '') + '" data-view="' + key + '">' + viewLabel(g, key) + '</button>';
    }).join('');
  }

  function renderPreview() {
    var g = state.garment;
    var view = activeView();
    var zone = zoneFor(view);
    var requestedView = view, requestedGarment = g;
    var photo = isPhotoGarment(g);

    var aspect = photo ? '1/1' : (function () {
      var vb = g.viewBox.split(' ').map(Number);
      return vb[2] + '/' + vb[3];
    })();
    els.canvasWrap.style.aspectRatio = aspect;
    els.canvasWrap.classList.toggle('dt-canvas-wrap--photo', photo);
    // How far down the garment starts by default — some artwork (traced
    // straight off a photo) has almost no built-in headroom above the
    // collar, so it needs more top padding to clear the toolbar/tabs above;
    // others (hand-drawn) already have generous headroom baked in.
    els.canvasStage.style.paddingTop = (g.topPadPct != null ? g.topPadPct : 3) + '%';

    els.canvasWrap.innerHTML =
      '<div class="dt-photo-stage" id="dt-photo-stage">' +
        '<img class="dt-photo-garment" id="dt-photo-garment-img" alt="' + g.name + '">' +
        '<div class="dt-photo-zone" id="dt-photo-zone" style="left:' + zone.leftPct + '%; top:' + zone.topPct + '%; width:' + zone.wPct + '%; height:' + zone.hPct + '%;"></div>' +
      '</div>';

    getGarmentPreviewSrc(g, state.color.hex, view).then(function (src) {
      if (state.garment !== requestedGarment || activeView() !== requestedView) return;
      var imgEl = document.getElementById('dt-photo-garment-img');
      if (imgEl) {
        // Print's live price estimate (see currentViewPrintEstimate) reads
        // this image's naturalWidth/Height straight off the DOM — refresh
        // the info panel once it's actually decoded and ready, instead of
        // leaving "A medir o tamanho…" showing until some unrelated change
        // happens to re-render it.
        imgEl.onload = function () { renderInfoPanel(); updateSliderReadout(); };
        imgEl.src = src;
      }
    });

    renderPhotoZoneContent();
    bindPhotoZoneDrag();
    renderLayersPanel();
    renderElementToolbar();

    var multi = hasMultipleViews();
    els.viewTabs.hidden = !multi;
    if (multi) renderViewTabs();


    applyZoomPan();
  }

  // ─── Layers (multi-element design) ─────────────────────────────────────
  // An uploaded image and a rendered-text image are stored identically (see
  // applyTextLayer/logoInput handler below) — both just push a layer here.
  var layerIdCounter = 0;
  function nextLayerId() { return 'layer-' + (++layerIdCounter) + '-' + Date.now(); }

  function addLayer(type, url, label) {
    var layers = currentLayers();
    if (layers.length >= MAX_LAYERS) {
      showToast(t('maxLayersAdd', { max: MAX_LAYERS }), 'error');
      return null;
    }
    // Cascade each new element's default position slightly so several in a
    // row don't land exactly on top of each other.
    var n = layers.length;
    var offset = (n % 3) * 0.07 - 0.07;
    var layer = {
      id: nextLayerId(),
      type: type, // 'image' | 'text' — both render/composite identically; only used for the toolbar/panel UI
      url: url,
      label: label,
      scale: 1,
      rotation: 0,
      x: 0.5 + offset,
      y: 0.5 + offset,
    };
    layers.push(layer);
    state.activeLayerId = layer.id;
    return layer;
  }

  function cloneLayerData(layer) {
    var copy = {};
    for (var k in layer) { if (Object.prototype.hasOwnProperty.call(layer, k)) copy[k] = layer[k]; }
    copy.id = nextLayerId();
    return copy;
  }

  function duplicateLayer(id) {
    var layers = currentLayers();
    var idx = layers.findIndex(function (l) { return l.id === id; });
    if (idx === -1) return;
    if (layers.length >= MAX_LAYERS) {
      showToast(t('maxLayersDuplicate', { max: MAX_LAYERS }), 'error');
      return;
    }
    var copy = cloneLayerData(layers[idx]);
    copy.x = Math.min(0.95, layers[idx].x + 0.06);
    copy.y = Math.min(0.95, layers[idx].y + 0.06);
    layers.splice(idx + 1, 0, copy);
    state.activeLayerId = copy.id;
    renderLayersPanel();
    renderPhotoZoneContent();
    renderElementToolbar();
    renderInfoPanel();
    updateAddToCartState();
    pushHistory();
  }

  function activeLayer() {
    return currentLayers().find(function (l) { return l.id === state.activeLayerId; }) || null;
  }

  function selectLayer(id) {
    state.activeLayerId = id;
    renderLayersPanel();
    renderPhotoZoneContent();
    renderElementToolbar();
  }

  function removeLayer(id) {
    var layers = currentLayers();
    var idx = layers.findIndex(function (l) { return l.id === id; });
    if (idx === -1) return;
    layers.splice(idx, 1);
    if (state.activeLayerId === id) {
      state.activeLayerId = layers.length ? layers[layers.length - 1].id : null;
    }
    renderLayersPanel();
    renderPhotoZoneContent();
    renderElementToolbar();
    renderInfoPanel();
    updateAddToCartState();
    pushHistory();
  }

  function moveLayer(id, dir) {
    var layers = currentLayers();
    var idx = layers.findIndex(function (l) { return l.id === id; });
    var newIdx = idx + dir;
    if (idx === -1 || newIdx < 0 || newIdx >= layers.length) return;
    var tmp = layers[idx];
    layers[idx] = layers[newIdx];
    layers[newIdx] = tmp;
    renderLayersPanel();
    renderPhotoZoneContent();
    pushHistory();
  }

  function updateLayerControlsUI() {
    var layer = activeLayer();
    els.layerControls.hidden = !layer;
    if (layer) {
      els.sliderScale.value = layer.scale;
      els.sliderRotation.value = layer.rotation;
    }
    updateSliderReadout();
  }

  // Live "≈ 18cm" readout next to the Tamanho slider — real-world size of
  // the currently selected layer, using the same layerSizeCm formula the
  // Print pricing engine uses. Blank whenever it can't be computed yet
  // (no layer selected, garment photo not decoded, or this garment has no
  // realWidthCm calibration) rather than showing a wrong/misleading number.
  // Also owns two side effects that have to stay in lockstep with it: (1)
  // the slider's own MIN, raised so it can never be dragged below
  // MIN_LAYER_CM in real cm (a fixed scale like 0.2 means a different cm
  // size on every garment/zone, so this has to be recomputed here, not set
  // once in the liquid) — clamping the layer's own scale up to match if it's
  // already below that (e.g. just switched to a smaller zone); and (2) the
  // info panel's live Print price, which is keyed off the same per-layer cm
  // size and would otherwise go stale while dragging.
  function updateSliderReadout() {
    if (!els.sliderScaleReadout) return;
    var layer = activeLayer();
    var imgEl = document.getElementById('dt-photo-garment-img');
    if (!layer || !imgEl || !imgEl.naturalWidth || !state.garment) {
      els.sliderScaleReadout.textContent = '';
      if (els.sliderScale) els.sliderScale.min = SLIDER_SCALE_MIN_DEFAULT;
      return;
    }
    var zone = zoneFor(state.view);
    var base = layerBaseSizeCm(layer, zone, state.garment, imgEl.naturalWidth, imgEl.naturalHeight);
    if (base && base.maxCm > 0) {
      var minScale = MIN_LAYER_CM / base.maxCm;
      minScale = Math.max(0.05, Math.min(minScale, parseFloat(els.sliderScale.max) || 2));
      els.sliderScale.min = minScale.toFixed(3);
      if (layer.scale < minScale) {
        layer.scale = minScale;
        els.sliderScale.value = minScale;
        refreshLayerElements();
      }
    } else if (els.sliderScale) {
      els.sliderScale.min = SLIDER_SCALE_MIN_DEFAULT;
    }
    var size = layerSizeCm(layer, zone, state.garment, imgEl.naturalWidth, imgEl.naturalHeight);
    els.sliderScaleReadout.textContent = size ? ('≈ ' + Math.round(size.maxCm) + 'cm') : '';
    renderInfoPanel();
  }

  function renderLayersPanel() {
    if (!els.layersList) return;
    var layers = currentLayers();
    els.layersCount.textContent = t('elementsUsed', { count: layers.length, max: MAX_LAYERS });
    els.layersList.innerHTML = '';
    if (!layers.length) {
      var empty = document.createElement('p');
      empty.className = 'dt-hint';
      empty.textContent = t('noElementsYet');
      els.layersList.appendChild(empty);
    }
    layers.forEach(function (layer, i) {
      var row = document.createElement('div');
      row.className = 'dt-layer-row' + (layer.id === state.activeLayerId ? ' is-selected' : '');
      row.innerHTML =
        '<span class="dt-layer-row__thumb"><img src="' + layer.url + '" alt=""></span>' +
        '<span class="dt-layer-row__label">' + (layer.label || (layer.type === 'text' ? t('layerText') : t('layerImage'))) + '</span>' +
        '<span class="dt-layer-row__actions">' +
          '<button type="button" class="dt-layer-row__btn" data-move="up" ' + (i === 0 ? 'disabled' : '') + ' aria-label="' + t('moveUp') + '"></button>' +
          '<button type="button" class="dt-layer-row__btn" data-move="down" ' + (i === layers.length - 1 ? 'disabled' : '') + ' aria-label="' + t('moveDown') + '"></button>' +
          '<button type="button" class="dt-layer-row__btn dt-layer-row__btn--danger" data-delete aria-label="' + t('delete') + '"></button>' +
        '</span>';
      row.querySelector('[data-move="up"]').innerHTML = iconMarkup('chevron-up');
      row.querySelector('[data-move="down"]').innerHTML = iconMarkup('chevron-down');
      row.querySelector('[data-delete]').innerHTML = iconMarkup('trash');
      row.querySelector('.dt-layer-row__thumb').addEventListener('click', function () { selectLayer(layer.id); });
      row.querySelector('.dt-layer-row__label').addEventListener('click', function () { selectLayer(layer.id); });
      row.querySelector('[data-move="up"]').addEventListener('click', function (e) { e.stopPropagation(); moveLayer(layer.id, -1); });
      row.querySelector('[data-move="down"]').addEventListener('click', function (e) { e.stopPropagation(); moveLayer(layer.id, 1); });
      row.querySelector('[data-delete]').addEventListener('click', function (e) { e.stopPropagation(); removeLayer(layer.id); });
      els.layersList.appendChild(row);
    });
    updateLayerControlsUI();
  }

  // Sizes + positions a single .dt-layer wrapper to match its layer's
  // scale/x/y/rotation, relative to the zone's CURRENT rendered pixel size.
  // Width/height are set as explicit pixels (not CSS max-width/max-height
  // percentages) precisely so that dragging — which only ever changes
  // left/top — can never change them: percentage sizing on an auto-width
  // absolutely-positioned box is computed from the space available *after*
  // `left`, so it would silently resize as `left` changes (this was the
  // "changes size while dragging" bug).
  function sizeLayerElement(wrap, layer, zoneRect) {
    var aspect = layer._aspect || 1;
    var boxW = zoneRect.width * 0.6, boxH = zoneRect.height * 0.6;
    var boxAspect = boxW / boxH;
    var baseW, baseH;
    if (aspect > boxAspect) { baseW = boxW; baseH = boxW / aspect; }
    else { baseH = boxH; baseW = boxH * aspect; }
    wrap.style.width = (baseW * layer.scale) + 'px';
    wrap.style.height = (baseH * layer.scale) + 'px';
  }
  function positionLayerElement(wrap, layer) {
    wrap.style.left = (layer.x * 100) + '%';
    wrap.style.top = (layer.y * 100) + '%';
    wrap.style.transform = 'translate(-50%, -50%) rotate(' + layer.rotation + 'deg)';
  }

  function renderPhotoZoneContent() {
    var zoneEl = document.getElementById('dt-photo-zone');
    if (!zoneEl) return;
    zoneEl.innerHTML = '';
    var layers = currentLayers();
    if (!layers.length) {
      zoneEl.classList.remove('has-logo');
      var hint = document.createElement('div');
      hint.className = 'dt-photo-zone__hint';
      hint.textContent = t('uploadHint');
      zoneEl.appendChild(hint);
      return;
    }
    zoneEl.classList.add('has-logo');
    var zoneRect = zoneEl.getBoundingClientRect();
    layers.forEach(function (layer) {
      var wrap = document.createElement('div');
      wrap.className = 'dt-layer' + (layer.id === state.activeLayerId ? ' is-active' : '');
      wrap.dataset.layerId = layer.id;
      positionLayerElement(wrap, layer);
      var img = document.createElement('img');
      img.className = 'dt-layer__img';
      if (layer._aspect) {
        sizeLayerElement(wrap, layer, zoneRect);
      } else {
        img.onload = function () {
          layer._aspect = img.naturalWidth / img.naturalHeight || 1;
          sizeLayerElement(wrap, layer, zoneRect);
        };
      }
      img.src = layer.url;
      wrap.appendChild(img);
      zoneEl.appendChild(wrap);
    });
    updateLayerControlsUI();
  }

  // Re-applies every current-view layer's on-screen size/position without
  // rebuilding the DOM or reloading images — used after a slider/toolbar
  // edit (fast, no flicker) and on window resize (the zone's pixel size may
  // have changed, so each layer's explicit px size needs recomputing).
  function refreshLayerElements() {
    var zoneEl = document.getElementById('dt-photo-zone');
    if (!zoneEl) return;
    var zoneRect = zoneEl.getBoundingClientRect();
    currentLayers().forEach(function (layer) {
      var wrap = zoneEl.querySelector('[data-layer-id="' + layer.id + '"]');
      if (!wrap) return;
      positionLayerElement(wrap, layer);
      if (layer._aspect) sizeLayerElement(wrap, layer, zoneRect);
    });
  }
  window.addEventListener('resize', refreshLayerElements);

  function bindPhotoZoneDrag() {
    var zoneEl = document.getElementById('dt-photo-zone');
    var stage = document.getElementById('dt-photo-stage');
    if (!zoneEl || !stage) return;
    var draggingEl = null, offsetX = 0, offsetY = 0;
    // Two-finger pinch-to-resize / twist-to-rotate — a touch-only equivalent
    // of the Tamanho/Rotação sliders in the Layers tab, driven straight off
    // the element instead. Starts the moment a 2nd finger comes down while a
    // layer is already being touched (from the 1-finger touchstart below),
    // so it works whether the 2nd finger lands inside the small placement
    // zone or not — real pinch gestures usually spread past it.
    var pinching = false;
    var pinchStartDist = 0, pinchStartAngle = 0, pinchStartScale = 1, pinchStartRotation = 0;

    function touchDist(t0, t1) { return Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY); }
    function touchAngle(t0, t1) { return Math.atan2(t1.clientY - t0.clientY, t1.clientX - t0.clientX) * 180 / Math.PI; }
    // Same real-world floor as the Tamanho slider (see updateSliderReadout /
    // MIN_LAYER_CM) — a pinch must not be able to shrink a design smaller
    // than dragging the slider itself would allow.
    function pinchMinScale(layer) {
      var imgEl = document.getElementById('dt-photo-garment-img');
      if (!imgEl || !imgEl.naturalWidth || !state.garment) return 0.05;
      var base = layerBaseSizeCm(layer, zoneFor(state.view), state.garment, imgEl.naturalWidth, imgEl.naturalHeight);
      return base && base.maxCm ? Math.max(0.05, MIN_LAYER_CM / base.maxCm) : 0.05;
    }
    function draggingLayer() {
      return draggingEl && currentLayers().find(function (l) { return l.id === draggingEl.dataset.layerId; });
    }

    function start(clientX, clientY, target) {
      if (state.panMode) return;
      var layerEl = target && target.closest && target.closest('.dt-layer');
      if (!layerEl) return;
      var id = layerEl.dataset.layerId;
      state.activeLayerId = id;
      zoneEl.querySelectorAll('.dt-layer').forEach(function (el) {
        el.classList.toggle('is-active', el === layerEl);
      });
      renderLayersPanel();
      renderElementToolbar();
      var rect = layerEl.getBoundingClientRect();
      offsetX = clientX - (rect.left + rect.width / 2);
      offsetY = clientY - (rect.top + rect.height / 2);
      draggingEl = layerEl;
    }
    function move(clientX, clientY) {
      if (!draggingEl || pinching) return;
      var zoneRect = zoneEl.getBoundingClientRect();
      var newX = (clientX - offsetX - zoneRect.left) / zoneRect.width;
      var newY = (clientY - offsetY - zoneRect.top) / zoneRect.height;
      newX = Math.max(0, Math.min(1, newX));
      newY = Math.max(0, Math.min(1, newY));
      var layer = currentLayers().find(function (l) { return l.id === draggingEl.dataset.layerId; });
      if (layer) { layer.x = newX; layer.y = newY; }
      draggingEl.style.left = (newX * 100) + '%';
      draggingEl.style.top = (newY * 100) + '%';
    }
    function startPinch(t0, t1) {
      var layer = draggingLayer();
      if (!layer) return;
      pinching = true;
      pinchStartDist = touchDist(t0, t1);
      pinchStartAngle = touchAngle(t0, t1);
      pinchStartScale = layer.scale;
      pinchStartRotation = layer.rotation;
    }
    function movePinch(t0, t1) {
      var layer = draggingLayer();
      if (!pinching || !layer) return;
      var ratio = pinchStartDist > 0 ? touchDist(t0, t1) / pinchStartDist : 1;
      var maxScale = parseFloat(els.sliderScale.max) || 2;
      layer.scale = Math.max(pinchMinScale(layer), Math.min(maxScale, pinchStartScale * ratio));
      var rot = pinchStartRotation + (touchAngle(t0, t1) - pinchStartAngle);
      // Wrap into [-180, 180] — same range as the Rotação slider.
      layer.rotation = Math.round(((rot + 180) % 360 + 360) % 360 - 180);
      refreshLayerElements();
      updateLayerControlsUI(); // syncs both sliders + the cm readout + live info-panel price
    }
    function end() {
      if (draggingEl) pushHistory();
      draggingEl = null;
      pinching = false;
    }

    zoneEl.addEventListener('mousedown', function (e) { start(e.clientX, e.clientY, e.target); if (draggingEl) e.preventDefault(); });
    window.addEventListener('mousemove', function (e) { move(e.clientX, e.clientY); });
    window.addEventListener('mouseup', end);

    zoneEl.addEventListener('touchstart', function (e) {
      if (e.touches.length === 1) { var t = e.touches[0]; start(t.clientX, t.clientY, e.target); }
    }, { passive: true });
    // 2nd finger listened for on window, not zoneEl — a real pinch's second
    // finger routinely lands outside the (often small) placement zone.
    window.addEventListener('touchstart', function (e) {
      if (draggingEl && !pinching && e.touches.length >= 2) startPinch(e.touches[0], e.touches[1]);
    }, { passive: true });
    window.addEventListener('touchmove', function (e) {
      if (pinching && e.touches.length >= 2) { movePinch(e.touches[0], e.touches[1]); return; }
      if (e.touches.length === 1) { var t = e.touches[0]; move(t.clientX, t.clientY); }
    }, { passive: true });
    window.addEventListener('touchend', function (e) {
      if (pinching && e.touches.length < 2) {
        // Dropped from 2 fingers to 1 (or 0) — leave pinch mode. If one
        // finger is still down, recompute its drag offset from here so the
        // layer doesn't jump to align with the pinch's old center point.
        pinching = false;
        if (e.touches.length === 1 && draggingEl) {
          var t = e.touches[0];
          var rect = draggingEl.getBoundingClientRect();
          offsetX = t.clientX - (rect.left + rect.width / 2);
          offsetY = t.clientY - (rect.top + rect.height / 2);
        }
      }
      if (e.touches.length === 0) end();
    });
  }

  els.viewTabs.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-view]');
    if (!btn) return;
    state.view = btn.getAttribute('data-view');
    els.viewTabs.querySelectorAll('[data-view]').forEach(function (b) { b.classList.remove('is-active'); });
    btn.classList.add('is-active');
    renderPreview();
  });

  // ─── Zoom / pan ─────────────────────────────────────────────────────────
  function applyZoomPan() {
    els.canvasStage.style.transform = 'translate(' + state.panX + 'px,' + state.panY + 'px) scale(' + state.zoom + ')';
    els.zoomPct.textContent = Math.round(state.zoom * 100) + '%';
  }
  els.zoomIn.addEventListener('click', function () {
    state.zoom = Math.min(3, +(state.zoom + 0.2).toFixed(2));
    applyZoomPan();
  });
  els.zoomOut.addEventListener('click', function () {
    state.zoom = Math.max(0.5, +(state.zoom - 0.2).toFixed(2));
    applyZoomPan();
  });
  els.panToggle.addEventListener('click', function () {
    state.panMode = !state.panMode;
    els.panToggle.classList.toggle('is-active', state.panMode);
    els.canvasViewport.classList.toggle('is-pan-mode', state.panMode);
  });
  // Mouse wheel over the canvas zooms the product (there's no page scroll to
  // hijack on desktop — the whole tool fits in one viewport by design).
  els.canvasViewport.addEventListener('wheel', function (e) {
    e.preventDefault();
    state.zoom = Math.min(3, Math.max(0.5, +(state.zoom - e.deltaY * 0.0015).toFixed(2)));
    applyZoomPan();
  }, { passive: false });
  (function bindPanDrag() {
    var dragging = false, startX = 0, startY = 0, startPanX = 0, startPanY = 0;
    function start(clientX, clientY) {
      if (!state.panMode) return;
      dragging = true;
      startX = clientX; startY = clientY;
      startPanX = state.panX; startPanY = state.panY;
      els.canvasViewport.classList.add('is-panning');
    }
    function move(clientX, clientY) {
      if (!dragging) return;
      state.panX = startPanX + (clientX - startX);
      state.panY = startPanY + (clientY - startY);
      applyZoomPan();
    }
    function end() { dragging = false; els.canvasViewport.classList.remove('is-panning'); }
    els.canvasViewport.addEventListener('mousedown', function (e) { start(e.clientX, e.clientY); });
    window.addEventListener('mousemove', function (e) { move(e.clientX, e.clientY); });
    window.addEventListener('mouseup', end);
    els.canvasViewport.addEventListener('touchstart', function (e) { var t = e.touches[0]; start(t.clientX, t.clientY); }, { passive: true });
    window.addEventListener('touchmove', function (e) { if (state.panMode) { var t = e.touches[0]; move(t.clientX, t.clientY); } }, { passive: true });
    window.addEventListener('touchend', end);
  })();

  // ─── Logo upload ───────────────────────────────────────────────────────
  // Shared by uploaded file(s) and by the "Add Text" tool below — either way
  // the result is just a new layer, with the same drag/scale/rotate controls
  // and the same cart-export path.
  function addLayerAndNotify(type, dataUrl, label, extra) {
    var layer = addLayer(type, dataUrl, label);
    if (!layer) return null; // cap reached — addLayer already showed the toast
    if (extra) { for (var k in extra) layer[k] = extra[k]; }
    renderLayersPanel();
    renderPhotoZoneContent();
    renderElementToolbar();
    renderInfoPanel();
    updateAddToCartState();
    showToast(t('addedDragToReposition'), 'success');
    pushHistory();
    setActivePanel('layers');
    return layer;
  }

  function readFileAsDataUrl(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () { resolve(reader.result); };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  els.logoTrigger.addEventListener('click', function () { els.logoInput.click(); });
  els.logoInput.addEventListener('change', function (e) {
    var files = e.target.files ? Array.prototype.slice.call(e.target.files) : [];
    if (!files.length) return;
    // Read sequentially so each addLayer() call sees the up-to-date count
    // (and stops cleanly with a toast once the 5-element cap is hit).
    var chain = Promise.resolve();
    files.forEach(function (file) {
      chain = chain.then(function () {
        return readFileAsDataUrl(file).then(function (dataUrl) {
          addLayerAndNotify('image', dataUrl, file.name);
        });
      });
    });
    chain.catch(function () { showToast(t('couldNotReadFile'), 'error'); });
    els.logoInput.value = ''; // allow re-selecting the same file(s) later
  });

  // ─── Add Text (no upload needed) ────────────────────────────────────────
  // Rasterises the typed text onto a transparent canvas and feeds it through
  // the exact same pipeline as an uploaded logo image. Re-run whenever a
  // text layer's font/colour/bold/italic/align changes (see updateTextLayer)
  // so those stay editable after the element has already been placed.
  function renderTextToDataUrl(text, fontFamily, color, opts) {
    opts = opts || {};
    var bold = opts.bold !== false; // default true, matches the tool's original always-bold look
    var italic = !!opts.italic;
    var align = opts.align === 'left' || opts.align === 'right' ? opts.align : 'center';
    var fontSize = 140;
    var lineHeight = fontSize * 1.25;
    var lines = text.split('\n');
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    var fontStr = (italic ? 'italic ' : '') + (bold ? '700 ' : '400 ') + fontSize + 'px ' + fontFamily;
    return document.fonts.load(fontStr).catch(function () {}).then(function () {
      ctx.font = fontStr;
      var maxWidth = 0;
      lines.forEach(function (line) { maxWidth = Math.max(maxWidth, ctx.measureText(line).width); });
      canvas.width = Math.ceil(maxWidth) + 40;
      canvas.height = Math.ceil(lineHeight * lines.length) + 40;
      // Resizing the canvas resets its context state, so the font has to be set again.
      ctx.font = fontStr;
      ctx.fillStyle = color;
      ctx.textAlign = align;
      ctx.textBaseline = 'middle';
      var anchorX = align === 'left' ? 20 : align === 'right' ? canvas.width - 20 : canvas.width / 2;
      lines.forEach(function (line, i) {
        ctx.fillText(line, anchorX, 20 + lineHeight * i + lineHeight / 2);
      });
      return canvas.toDataURL('image/png');
    });
  }

  els.textAdd.addEventListener('click', function () {
    var text = (els.textInput.value || '').trim();
    if (!text) { showToast(t('typeSomethingFirst'), 'error'); return; }
    if (currentLayers().length >= MAX_LAYERS) {
      showToast(t('maxLayersAdd', { max: MAX_LAYERS }), 'error');
      return;
    }
    els.textAdd.disabled = true;
    var textOpts = { bold: true, italic: false, align: 'center' };
    renderTextToDataUrl(text, els.textFont.value, els.textColor.value, textOpts).then(function (dataUrl) {
      addLayerAndNotify('text', dataUrl, 'Text: "' + text + '"', {
        text: text, font: els.textFont.value, color: els.textColor.value,
        bold: textOpts.bold, italic: textOpts.italic, align: textOpts.align,
      });
      els.textAdd.disabled = false;
    }).catch(function () {
      showToast(t('couldNotRenderText'), 'error');
      els.textAdd.disabled = false;
    });
  });

  // Re-rasterises a text layer after an edit from the contextual toolbar
  // (font/colour/bold/italic/align) — the underlying image swaps, so the
  // aspect ratio is re-measured on next render too.
  function updateTextLayer(layer, changes) {
    for (var k in changes) layer[k] = changes[k];
    return renderTextToDataUrl(layer.text, layer.font, layer.color, { bold: layer.bold, italic: layer.italic, align: layer.align }).then(function (dataUrl) {
      layer.url = dataUrl;
      layer._aspect = null;
      renderLayersPanel();
      renderPhotoZoneContent();
      pushHistory();
    });
  }

  // ─── Contextual element toolbar ─────────────────────────────────────────
  // Shown above the canvas only while a layer is selected (replaces the old
  // Edit/Preview toggle). Text layers get font/size/bold/italic/align/colour
  // controls (re-rendering the rasterised text on change); every layer type
  // gets Duplicate, Delete, and — on garments with more than one print area —
  // "Apply to all areas" to copy it into every other area at once.
  var FONT_OPTIONS = Array.prototype.map.call(els.textFont.options, function (o) {
    return { value: o.value, label: o.textContent };
  });

  function renderElementToolbar() {
    if (!els.elementToolbar) return;
    var layer = activeLayer();
    els.elementToolbar.innerHTML = '';
    els.elementToolbar.hidden = !layer;
    if (els.modeToggle) els.modeToggle.hidden = !!layer;
    if (!layer) return;

    var posLabel = document.createElement('span');
    posLabel.className = 'dt-el-toolbar__position';
    posLabel.textContent = t('position');
    els.elementToolbar.appendChild(posLabel);

    if (layer.type === 'text') {
      var fontSel = document.createElement('select');
      fontSel.className = 'dt-el-toolbar__font';
      FONT_OPTIONS.forEach(function (f) {
        var opt = document.createElement('option');
        opt.value = f.value;
        opt.textContent = f.label;
        if (f.value === layer.font) opt.selected = true;
        fontSel.appendChild(opt);
      });
      fontSel.addEventListener('change', function () { updateTextLayer(layer, { font: fontSel.value }); });
      els.elementToolbar.appendChild(fontSel);

      var sizeInput = document.createElement('input');
      sizeInput.type = 'number';
      sizeInput.min = '20'; sizeInput.max = '200'; sizeInput.step = '5';
      sizeInput.className = 'dt-el-toolbar__size';
      sizeInput.title = t('sizePct');
      sizeInput.value = Math.round(layer.scale * 100);
      sizeInput.addEventListener('change', function () {
        var v = parseFloat(sizeInput.value);
        if (isNaN(v)) v = 100;
        layer.scale = Math.max(0.2, Math.min(2, v / 100));
        refreshLayerElements();
        updateLayerControlsUI();
        pushHistory();
      });
      els.elementToolbar.appendChild(sizeInput);

      var boldBtn = document.createElement('button');
      boldBtn.type = 'button';
      boldBtn.className = 'dt-el-toolbar__btn' + (layer.bold ? ' is-active' : '');
      boldBtn.style.fontWeight = '700';
      boldBtn.textContent = 'B';
      boldBtn.title = t('bold');
      boldBtn.addEventListener('click', function () { updateTextLayer(layer, { bold: !layer.bold }).then(renderElementToolbar); });
      els.elementToolbar.appendChild(boldBtn);

      var italicBtn = document.createElement('button');
      italicBtn.type = 'button';
      italicBtn.className = 'dt-el-toolbar__btn' + (layer.italic ? ' is-active' : '');
      italicBtn.style.fontStyle = 'italic';
      italicBtn.textContent = 'I';
      italicBtn.title = t('italic');
      italicBtn.addEventListener('click', function () { updateTextLayer(layer, { italic: !layer.italic }).then(renderElementToolbar); });
      els.elementToolbar.appendChild(italicBtn);

      ['left', 'center', 'right'].forEach(function (a) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'dt-el-toolbar__btn' + (layer.align === a ? ' is-active' : '');
        b.innerHTML = iconMarkup('align-' + a);
        b.title = t('align' + a.charAt(0).toUpperCase() + a.slice(1));
        b.addEventListener('click', function () { updateTextLayer(layer, { align: a }).then(renderElementToolbar); });
        els.elementToolbar.appendChild(b);
      });

      var colorInput = document.createElement('input');
      colorInput.type = 'color';
      colorInput.className = 'dt-el-toolbar__color';
      colorInput.value = layer.color;
      colorInput.title = t('colour');
      colorInput.addEventListener('input', function () { updateTextLayer(layer, { color: colorInput.value }); });
      els.elementToolbar.appendChild(colorInput);

      var divider = document.createElement('span');
      divider.className = 'dt-el-toolbar__divider';
      els.elementToolbar.appendChild(divider);
    }

    if (layer.type === 'image') {
      var bgBtn = document.createElement('button');
      bgBtn.type = 'button';
      bgBtn.className = 'dt-el-toolbar__apply-all';
      bgBtn.textContent = t('removeBackground');
      bgBtn.addEventListener('click', function () {
        bgBtn.disabled = true;
        var originalLabel = bgBtn.textContent;
        bgBtn.textContent = t('removingBackground');
        function applyResult(dataUrl) {
          layer.url = dataUrl;
          layer._aspect = null; // re-measure — transparent edges can shift the visible aspect ratio
          renderLayersPanel();
          renderPhotoZoneContent();
          bgBtn.disabled = false;
          bgBtn.textContent = originalLabel;
          showToast(t('backgroundRemoved'), 'success');
          pushHistory();
        }
        removeBackgroundAI(layer.url).then(applyResult).catch(function () {
          // AI path failed (blocked script, unsupported browser, first-load
          // network hiccup, ...) — fall back to the instant plain-white
          // remover rather than leaving the customer with nothing.
          removeWhiteBackground(layer.url).then(applyResult).catch(function () {
            showToast(t('couldNotRemoveBackground'), 'error');
            bgBtn.disabled = false;
            bgBtn.textContent = originalLabel;
          });
        });
      });
      els.elementToolbar.appendChild(bgBtn);

      var imgDivider = document.createElement('span');
      imgDivider.className = 'dt-el-toolbar__divider';
      els.elementToolbar.appendChild(imgDivider);
    }

    var dupBtn = document.createElement('button');
    dupBtn.type = 'button';
    dupBtn.className = 'dt-el-toolbar__btn';
    dupBtn.innerHTML = iconMarkup('copy');
    dupBtn.title = t('duplicate');
    dupBtn.addEventListener('click', function () { duplicateLayer(layer.id); });
    els.elementToolbar.appendChild(dupBtn);

    var delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'dt-el-toolbar__btn dt-el-toolbar__btn--danger';
    delBtn.innerHTML = iconMarkup('trash');
    delBtn.title = t('delete');
    delBtn.addEventListener('click', function () { removeLayer(layer.id); });
    els.elementToolbar.appendChild(delBtn);
  }

  function bindLayerSlider(el, key, parse) {
    el.addEventListener('input', function () {
      var layer = activeLayer();
      if (!layer) return;
      layer[key] = parse(el.value);
      refreshLayerElements();
      updateSliderReadout();
    });
    // Snapshot on release (not every 'input' tick) so dragging a slider
    // doesn't flood the undo stack.
    el.addEventListener('change', function () { pushHistory(); });
  }
  bindLayerSlider(els.sliderScale, 'scale', parseFloat);
  bindLayerSlider(els.sliderRotation, 'rotation', function (v) { return parseInt(v, 10); });

  // ─── Canvas compositing (shared by Download + Add to Cart) ────────────
  function loadImage(src) { return loadImageEl(src); }

  function drawBrandFooter(ctx, W, GARMENT_H, BAR_H, logo) {
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, GARMENT_H, W, BAR_H);
    ctx.strokeStyle = '#C9A84C';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, GARMENT_H);
    ctx.lineTo(W, GARMENT_H);
    ctx.stroke();

    var drawBrandText = function () {
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#C9A84C';
      ctx.font = 'bold 22px Georgia, serif';
      ctx.fillText('Conquistador Bordado', 150, GARMENT_H + 44);
      ctx.fillStyle = '#d1d5db';
      ctx.font = '14px Arial, sans-serif';
      ctx.fillText('\u{1F4DE} ' + (root.dataset.phoneDisplay || ''), 150, GARMENT_H + 70);
      ctx.fillStyle = '#4ade80';
      ctx.fillText('\u{1F4AC} WhatsApp: ' + (root.dataset.whatsappDisplay || ''), 150, GARMENT_H + 92);
      ctx.fillStyle = '#9ca3af';
      ctx.font = 'italic 12px Arial, sans-serif';
      ctx.fillText(root.dataset.address || '', 150, GARMENT_H + 112);
      ctx.fillStyle = '#6b7280';
      ctx.font = 'italic 11px Georgia, serif';
      ctx.fillText(t('yourDesignYourStyle'), 150, GARMENT_H + 136);
    };

    if (logo) {
      return loadImage(logo.url).then(function (logoImg) {
        var maxSize = 120;
        var aspect = logoImg.width / logoImg.height;
        var w = aspect >= 1 ? maxSize : maxSize * aspect;
        var h = aspect >= 1 ? maxSize / aspect : maxSize;
        ctx.drawImage(logoImg, 20 + (maxSize - w) / 2, GARMENT_H + 20 + (maxSize - h) / 2, w, h);
        drawBrandText();
      });
    }
    ctx.beginPath();
    ctx.arc(80, GARMENT_H + 80, 40, 0, Math.PI * 2);
    ctx.fillStyle = '#C9A84C';
    ctx.fill();
    ctx.fillStyle = '#1a1a1a';
    ctx.font = 'bold 36px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.fillText('C', 80, GARMENT_H + 93);
    ctx.textAlign = 'left';
    drawBrandText();
    return Promise.resolve();
  }

  // Composites one garment view onto a plain square canvas (no footer — used
  // for the per-view cart attachments) and resolves with a Blob.
  function compositeGarmentView(view) {
    var canvas = document.createElement('canvas');
    var W = 1000, H = 1000;
    canvas.width = W;
    canvas.height = H;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    var zone = zoneFor(view);
    var layers = layersForView(view);

    return getGarmentPreviewSrc(state.garment, state.color.hex, view).then(loadImage).then(function (garmentImg) {
      var scale = Math.min(W / garmentImg.width, H / garmentImg.height);
      var drawW = garmentImg.width * scale, drawH = garmentImg.height * scale;
      var offsetX = (W - drawW) / 2, offsetY = (H - drawH) / 2;
      ctx.drawImage(garmentImg, offsetX, offsetY, drawW, drawH);

      if (!layers.length) return canvasToBlob(canvas);
      var zoneX = offsetX + (zone.leftPct / 100) * drawW;
      var zoneY = offsetY + (zone.topPct / 100) * drawH;
      var zoneW = (zone.wPct / 100) * drawW;
      var zoneH = (zone.hPct / 100) * drawH;

      // Each layer's own unscaled box is capped at 60% of the zone (matching
      // .dt-layer's max-width/max-height in CSS) before its own scale
      // multiplier applies, so several elements can coexist without each
      // one defaulting to filling the whole zone.
      var chain = Promise.resolve();
      layers.forEach(function (layer) {
        chain = chain.then(function () {
          return loadImage(layer.url).then(function (layerImg) {
            var imgAspect = layerImg.width / layerImg.height;
            var boxW = zoneW * 0.6, boxH = zoneH * 0.6;
            var boxAspect = boxW / boxH;
            var baseW, baseH;
            if (imgAspect > boxAspect) { baseW = boxW; baseH = boxW / imgAspect; }
            else { baseH = boxH; baseW = boxH * imgAspect; }
            var drawLayerW = baseW * layer.scale, drawLayerH = baseH * layer.scale;
            var cx = zoneX + layer.x * zoneW, cy = zoneY + layer.y * zoneH;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate((layer.rotation * Math.PI) / 180);
            ctx.drawImage(layerImg, -drawLayerW / 2, -drawLayerH / 2, drawLayerW, drawLayerH);
            ctx.restore();
          });
        });
      });
      return chain.then(function () { return canvasToBlob(canvas); });
    });
  }

  function canvasToBlob(canvas) {
    return new Promise(function (resolve) {
      canvas.toBlob(function (blob) { resolve(blob); }, 'image/png');
    });
  }

  function blobToFile(blob, name) {
    return new File([blob], name, { type: 'image/png' });
  }

  // ─── Download ───────────────────────────────────────────────────────────
  els.downloadBtn.addEventListener('click', function () {
    els.downloadBtn.disabled = true;
    compositeGarmentView(activeView()).then(function (blob) {
      var link = document.createElement('a');
      link.download = 'conquistador-design-' + state.garment.id + '-' + activeView() + '.png';
      link.href = URL.createObjectURL(blob);
      link.click();
    }).then(function () {
      els.downloadBtn.disabled = false;
    }).catch(function () {
      showToast(t('couldNotGenerateMockup'), 'error');
      els.downloadBtn.disabled = false;
    });
  });

  // Bordado (Embroidery) has no fixed online price any more — the owner
  // decided it's quote-only, negotiated by hand. So an Embroidery order
  // never touches Shopify's cart at all: this downloads every design file
  // (the composited mockup AND the original artwork, same files production
  // would otherwise get from the cart line's properties) straight to the
  // customer's device, then opens WhatsApp with an order summary pre-filled.
  // wa.me links can only pre-fill TEXT — WhatsApp has no way to pre-attach a
  // file via a link — so the files are downloaded first and the message
  // explicitly asks the customer to attach them in the chat.
  function sendEmbroideryQuoteViaWhatsapp() {
    var g = state.garment;
    var designKeys = viewKeys().filter(function (key) { return layersForView(key).length > 0; });

    // Both the mockup (composited onto the actual garment) and the
    // customer's original uploaded artwork need to reach us — built as real
    // File objects up front, used by whichever path below actually runs.
    var mockupFilesPromise = Promise.all(designKeys.map(function (key) {
      return compositeGarmentView(key).then(function (blob) {
        return blobToFile(blob, 'conquistador-design-' + g.id + '-' + key + '.png');
      });
    }));
    var artFilesPromise = Promise.all(
      designKeys.reduce(function (acc, key) {
        layersForView(key).forEach(function (layer, i) {
          var extMatch = /^data:image\/(\w+)/.exec(layer.url);
          var name = 'conquistador-arte-original-' + g.id + '-' + key + '-' + (i + 1) + '.' + (extMatch ? extMatch[1] : 'png');
          acc.push(fetch(layer.url).then(function (r) { return r.blob(); }).then(function (blob) { return new File([blob], name, { type: blob.type || 'image/png' }); }));
        });
        return acc;
      }, [])
    );

    Promise.all([mockupFilesPromise, artFilesPromise]).then(function (results) {
      var allFiles = results[0].concat(results[1]);
      var colorBreakdown = (colorQtyEntries(g) || []).map(function (e) { return e.name + ' × ' + e.qty; }).join(', ') || (state.color.name + ' × ' + state.qtyTotal);
      var sizeBreakdown = (sizeQtyEntries(g) || []).map(function (e) { return e.size + ' × ' + e.qty; }).join(', ');
      var lines = [
        t('embroideryQuoteIntro'),
        t('embroideryQuoteGarment', { garment: g.name }),
        t('embroideryQuoteColour', { breakdown: colorBreakdown }),
      ];
      if (sizeBreakdown) lines.push(t('embroideryQuoteSize', { breakdown: sizeBreakdown }));
      lines.push(t('embroideryQuotePositions', { count: positionsWithDesignCount() }));

      // Prefer the native share sheet when it can actually carry files — one
      // tap gets the mockup(s) + original art straight into a WhatsApp chat
      // as real attachments (the customer still has to pick the chat/number
      // themselves; the API has no way to pre-address a share the way a
      // wa.me link can). This is also the fix for mockups going missing:
      // several separate <a download> clicks fired from inside this async
      // function (compositeGarmentView isn't instant) land outside the
      // click event's "trusted user gesture" window on a lot of mobile
      // browsers, which silently block anything past the first download —
      // only the (synchronous, no compositing needed) original artwork was
      // ever reliably arriving. navigator.share has no such per-file limit.
      if (navigator.share && navigator.canShare && allFiles.length && navigator.canShare({ files: allFiles })) {
        lines.push(t('embroideryQuoteShareNote'));
        return navigator.share({ files: allFiles, text: lines.join('\n') }).then(function () {
          showToast(t('embroideryQuoteShared'), 'success');
        }).catch(function (err) {
          if (err && err.name === 'AbortError') return; // customer just cancelled the share sheet, not a real failure
          throw err;
        });
      }

      // Fallback (desktop, or a browser that can't share files yet):
      // download everything, then open a pre-addressed WhatsApp text with a
      // reminder to attach the files that were just downloaded.
      allFiles.forEach(function (file) {
        var link = document.createElement('a');
        link.download = file.name;
        link.href = URL.createObjectURL(file);
        link.click();
      });
      lines.push(t('embroideryQuoteAttachNote'));
      var number = root.dataset.whatsappNumber;
      window.open('https://wa.me/' + number + '?text=' + encodeURIComponent(lines.join('\n')), '_blank');
      showToast(t('embroideryQuoteSent'), 'success');
    }).catch(function () {
      showToast(t('couldNotGenerateMockup'), 'error');
    }).then(function () {
      els.addToCartBtn.disabled = false;
    });
  }

  // ─── Add to cart ────────────────────────────────────────────────────────
  els.cartForm.addEventListener('submit', function (evt) {
    evt.preventDefault();
    var realProduct = matchedProduct(state.garment);

    // Real matched product: the customer may have picked several sizes at
    // once, so this can mean several cart lines (one per size/quantity) plus
    // one fee line whose quantity matches the total garment count. Each line
    // is added over fetch (multipart, so the mockup file attaches to every
    // line) — no page navigation needed between them — then we land on /cart.
    if (realProduct) {
      var blockReason = addToCartBlockReason();
      if (blockReason) {
        showToast(blockReason, 'error');
        return;
      }

      if (totalLayerCount() > 0 && state.garment.showTechnique !== false && state.technique === 'Embroidery') {
        els.addToCartBtn.disabled = true;
        showToast(t('preparingQuote'));
        sendEmbroideryQuoteViaWhatsapp();
        return;
      }

      var variant = representativeVariant(state.garment);
      if (!variant) {
        showToast(t('notAvailableOnline'), 'error');
        return;
      }

      els.addToCartBtn.disabled = true;
      showToast(t('addingToCart'));

      // Only views the customer actually placed a design on — NOT every
      // view the garment has. This used to be plain viewKeys() (every
      // position, designed or not), which composited and attached a
      // "Custom Design" mockup for EVERY view unconditionally: a customer
      // who ordered a completely blank garment (no logo/text anywhere)
      // still got 4 "Custom Design - Front/Back/Left/Right" files attached
      // to their order — confirmed live on conquistadorapparel.com. Matches
      // the filter sendEmbroideryQuoteViaWhatsapp/computePrintFeeLines
      // already use for the same reason.
      var keys = viewKeys().filter(function (key) { return layersForView(key).length > 0; });
      var totalQty = state.qtyTotal;
      // Colour and size are independent breakdowns (not paired per unit —
      // see canAddToCart), so only ONE representative variant is actually
      // added to Shopify; the real colour/size split for production is
      // attached as its own line item properties, same as the mockup/technique.
      var colorBreakdown = (colorQtyEntries(state.garment) || []).map(function (e) { return e.name + ' × ' + e.qty; }).join(', ') || (state.color.name + ' × ' + totalQty);
      var sizeBreakdown = (sizeQtyEntries(state.garment) || []).map(function (e) { return e.size + ' × ' + e.qty; }).join(', ');

      // Technique only actually means anything when there's a design to
      // customise — with zero layers there's nothing to embroider/print, so
      // neither the "Technique" property nor its fee line should show up in
      // the cart. When there IS a design, the fee is added as its own linked
      // product line (tagged with a shared hidden "_Design Group" property)
      // so the cart page can keep it glued to this garment line — see
      // sections/main-cart.liquid + assets/cart.js, which hide its remove
      // control and cascade removal/qty from the garment line instead.
      var hasDesign = totalLayerCount() > 0;
      // A technique fee only makes sense for a garment that actually offers
      // a Technique choice (see showTechnique) — Mug/Bottle/etc. don't, so
      // charging an "Embroidery" fee on those was a real bug: state.technique
      // just sits at its unrelated default ('Embroidery') for a garment that
      // never shows the Technique panel to ever change it. (Bordado itself
      // never reaches this far any more — it's diverted to
      // sendEmbroideryQuoteViaWhatsapp above — but the guard stays in case a
      // future extra "Technique"-tagged product needs the old model.)
      var hasFee = hasDesign && state.garment.showTechnique !== false;
      var feeProduct = hasFee ? feeProductFor(state.technique) : null;
      var designGroup = feeProduct ? ('dtg-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8)) : null;
      // Print prices per VIEW by real cm size (see computePrintFeeLines) —
      // possibly several fee lines, one per position, instead of a single
      // combined one. Any other technique keeps the old single-line
      // Posições-based model (selectFeeVariant).
      var feeLinesPromise = !hasFee
        ? Promise.resolve([])
        : state.technique === 'Print'
          ? computePrintFeeLines()
          : Promise.resolve((function () {
              var v = selectFeeVariant(state.technique);
              return v ? [{ id: v.id, priceCents: v.priceCents }] : [];
            })());

      Promise.all(keys.map(function (key) { return compositeGarmentView(key); })).then(function (blobs) {
        var fd = new FormData();
        fd.append('id', variant.id);
        fd.append('quantity', totalQty);
        fd.append('properties[Garment]', state.garment.name + ' — ' + realProduct.title);
        if (hasFee) {
          fd.append('properties[Technique]', state.technique);
          fd.append('properties[Positions]', String(positionsWithDesignCount()));
        }
        fd.append('properties[Colour breakdown]', colorBreakdown);
        if (sizeBreakdown) fd.append('properties[Size breakdown]', sizeBreakdown);
        fd.append('properties[Logo Position]', hasDesign ? (totalLayerCount() + ' element(s) placed — see attached design') : 'no logo uploaded');
        if (designGroup) fd.append('properties[_Design Group]', designGroup);
        blobs.forEach(function (blob, i) {
          var key = keys[i];
          var label = viewLabel(state.garment, key);
          var filename = 'conquistador-design-' + state.garment.id + '-' + key + '.png';
          var propName = keys.length > 1 ? 'properties[Custom Design - ' + label + ']' : 'properties[Custom Design]';
          fd.append(propName, blob, filename);
        });

        // Also attach each placed element's own file exactly as the customer
        // has it now (their uploaded logo, or the rendered text) — the
        // composite mockups above are a photo of the finished product for
        // context, but production (embroidery/print) needs the clean source
        // artwork itself, not a photo of it sitting on a shirt. layer.url is
        // already a data: URI holding the exact original bytes (set via
        // FileReader.readAsDataURL on upload, or the rendered text canvas),
        // so this is a lossless re-attachment, not a re-compression.
        var artworkEntries = [];
        keys.forEach(function (key) {
          var layers = state.viewLayers[key] || [];
          var viewLbl = viewLabel(state.garment, key);
          layers.forEach(function (layer, i) {
            var name = layers.length > 1 ? ('Arte Original - ' + viewLbl + ' (' + (i + 1) + ')') : ('Arte Original - ' + viewLbl);
            var extMatch = /^data:image\/(\w+)/.exec(layer.url);
            artworkEntries.push({ name: name, url: layer.url, ext: extMatch ? extMatch[1] : 'png' });
          });
        });

        return Promise.all(artworkEntries.map(function (e) { return fetch(e.url).then(function (r) { return r.blob(); }); }))
          .then(function (artworkBlobs) {
            artworkBlobs.forEach(function (blob, i) {
              var e = artworkEntries[i];
              fd.append('properties[' + e.name + ']', blob, 'arte-original-' + (i + 1) + '.' + e.ext);
            });
            return fd;
          });
      }).then(function (fd) {
        return fetch('/cart/add.js', { method: 'POST', body: fd }).then(function (res) {
          if (!res.ok) throw new Error('add failed');
        }).then(function () {
          return feeLinesPromise;
        }).then(function (feeLines) {
          if (!feeLines.length) return;
          // Each fee line notes which view/position it's for and (Print
          // only) which size tier it landed in, so the cart/order clearly
          // shows why there might be several — not just one mystery amount.
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: feeLines.map(function (line) {
                var props = { '_Design Group': designGroup, '_Linked Fee': 'true' };
                if (line.viewLabel) props['Posição'] = line.viewLabel;
                if (line.tierLabel) props['Tamanho'] = line.tierLabel;
                return { id: line.id, quantity: totalQty, properties: props };
              }),
            }),
          }).then(function (res) {
            if (!res.ok) throw new Error('fee add failed');
          });
        });
      }).then(function () {
        window.location.href = '/cart';
      }).catch(function () {
        showToast(t('couldNotAddToCart'), 'error');
        els.addToCartBtn.disabled = false;
      });
      return;
    }

    // Legacy fallback: garment has no matched "Blanks" product yet — single
    // item via the native form submit (unchanged from before).
    var legacyProduct = GARMENT_PRODUCTS[state.garment.id];
    var variantId = legacyProduct && (legacyProduct.hasSizes ? legacyProduct.sizes[state.size] : legacyProduct.variantId);
    var productTitle = legacyProduct && legacyProduct.title;

    if (!variantId) {
      var needsSize = legacyProduct && legacyProduct.hasSizes && !state.size;
      if (needsSize) {
        showToast(t('chooseSizeFirst'), 'error');
      } else {
        showToast(t('notAvailableOnline'), 'error');
      }
      return;
    }
    els.addToCartBtn.disabled = true;
    showToast(t('addingToCart'));

    // Same fix as the matched-product path above: only views that actually
    // have a design, not every view the garment has.
    var legacyKeys = viewKeys().filter(function (key) { return layersForView(key).length > 0; });
    els.propFilesExtra.innerHTML = '';

    Promise.all(legacyKeys.map(function (key) { return compositeGarmentView(key); })).then(function (blobs) {
      blobs.forEach(function (blob, i) {
        var key = legacyKeys[i];
        var label = viewLabel(state.garment, key);
        var file = blobToFile(blob, 'conquistador-design-' + state.garment.id + '-' + key + '.png');
        var dt = new DataTransfer();
        dt.items.add(file);
        if (i === 0) {
          els.propFile.files = dt.files;
        } else {
          var input = document.createElement('input');
          input.type = 'file';
          input.className = 'visually-hidden';
          input.name = 'properties[Custom Design - ' + label + ']';
          input.files = dt.files;
          els.propFilesExtra.appendChild(input);
        }
      });
    }).then(function () {
      els.variantInput.value = variantId;
      els.propGarment.value = state.garment.name + (productTitle ? ' — ' + productTitle : '') + (state.size ? ', Size ' + state.size : '');
      els.propTechnique.value = totalLayerCount() ? state.technique : '';
      els.propColour.value = state.color.name + ' (custom embroidery/print colour — see attached design)';
      els.propPosition.value = totalLayerCount()
        ? (totalLayerCount() + ' element(s) placed' + (hasMultipleViews()
            ? ' across ' + viewKeys().map(function (k) { return viewLabel(state.garment, k); }).join(' + ') + ' — see attached photos'
            : ' — see attached photo'))
        : 'no logo uploaded';

      els.cartForm.submit();
    }).catch(function () {
      showToast(t('couldNotAddToCart'), 'error');
      els.addToCartBtn.disabled = false;
    });
  });

  // ─── WhatsApp fallback CTA ──────────────────────────────────────────────
  function updateWhatsappLink() {
    // els.whatsappCta no longer exists — the bottom bar's WhatsApp CTA was
    // replaced with the running total (see updateBottomBarTotal), but this
    // is still called from many spots that touch state (colour/garment/
    // technique changes etc.), so it stays a harmless no-op rather than
    // ripping out every call site.
    if (!els.whatsappCta) return;
    var number = root.dataset.whatsappNumber;
    var text = t('whatsappQuoteText', { colour: state.color.name, garment: state.garment.name });
    els.whatsappCta.href = 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);
  }

  // Bottom bar's running total — the SAME lineTotal renderInfoPanel already
  // computes (blank/product price + qty + customisation fee), just also
  // shown next to Add to Cart so it's visible without opening/scrolling to
  // the Informação do Produto card. null clears it (no garment confirmed
  // yet, or this garment has no matched real product to price at all).
  function updateBottomBarTotal(cents) {
    if (!els.bottomBarTotal) return;
    els.bottomBarTotal.innerHTML = cents == null ? '' :
      '<span class="dt-bottom-bar__total-label">' + t('total') + '</span>' +
      '<span class="dt-bottom-bar__total-amount">' + formatMoney(cents) + '</span>';
  }

  // ─── Language switch ────────────────────────────────────────────────────
  // The "Add Text" textarea's placeholder can't use the [data-lang-pt]/
  // [data-lang-en] span-hiding trick (placeholder text isn't visible DOM
  // content), so it's swapped directly here instead — same idea as
  // cart.js's updateNotePlaceholder() for the cart note field.
  function updateTextInputPlaceholder() {
    if (!els.textInput) return;
    var attr = currentLang() === 'en' ? 'data-placeholder-en' : 'data-placeholder-pt';
    els.textInput.placeholder = els.textInput.getAttribute(attr) || els.textInput.placeholder;
  }
  updateTextInputPlaceholder();
  document.addEventListener('langchange', function () {
    updateTextInputPlaceholder();
    renderColorPicker();
    renderSizePicker();
    renderLayersPanel();
    renderPhotoZoneContent();
    renderElementToolbar();
    renderInfoPanel();
    updateAddToCartState();
    updateWhatsappLink();
    if (tutActive) renderTutStep();
  });

  // ─── Tutorial: welcome prompt + step-by-step tour ──────────────────────
  // A first-visit popup asking whether the customer wants a walkthrough;
  // "yes" starts a spotlight-and-balloon tour over the rail buttons (each
  // step also opens that panel via setActivePanel, so the customer sees the
  // real content, not just the button). Choice is remembered in
  // localStorage so it only self-prompts once per browser — the help (?)
  // button in the header restarts it any time after that.
  var TUT_STORAGE_KEY = 'conquistador-dt-tutorial-seen';
  var TUT_STEPS = [
    { target: '[data-panel="garment"]', panel: 'garment', titleKey: 'tutStep1Title', textKey: 'tutStep1Text' },
    { target: '[data-panel="colour"]', panel: 'colour', titleKey: 'tutStep2Title', textKey: 'tutStep2Text' },
    { target: '[data-panel="technique"]', panel: 'technique', titleKey: 'tutStep3Title', textKey: 'tutStep3Text' },
    { target: '[data-panel="logo"]', panel: 'logo', titleKey: 'tutStep4Title', textKey: 'tutStep4Text' },
    { target: '[data-panel="text"]', panel: 'text', titleKey: 'tutStep5Title', textKey: 'tutStep5Text' },
    { target: '#dt-canvas-viewport', panel: 'layers', titleKey: 'tutStep6Title', textKey: 'tutStep6Text' },
    { target: '#dt-bottom-bar', panel: null, titleKey: 'tutStep7Title', textKey: 'tutStep7Text' },
  ];
  var tutActive = false;
  var tutStepIndex = 0;

  function positionTutStep(step, targetEl) {
    var rect = targetEl.getBoundingClientRect();
    var pad = 8;
    els.tutSpotlight.style.top = (rect.top - pad) + 'px';
    els.tutSpotlight.style.left = (rect.left - pad) + 'px';
    els.tutSpotlight.style.width = (rect.width + pad * 2) + 'px';
    els.tutSpotlight.style.height = (rect.height + pad * 2) + 'px';

    var balloon = els.tutBalloon;
    var bw = balloon.offsetWidth, bh = balloon.offsetHeight;
    var vw = window.innerWidth, vh = window.innerHeight;
    var gap = 16, edge = 12;
    var top, left;
    if (vw - rect.right > bw + gap + edge) {
      left = rect.right + gap;
      top = Math.min(Math.max(edge, rect.top), vh - bh - edge);
    } else if (vh - rect.bottom > bh + gap + edge) {
      top = rect.bottom + gap;
      left = Math.min(Math.max(edge, rect.left), vw - bw - edge);
    } else if (rect.top > bh + gap + edge) {
      top = rect.top - bh - gap;
      left = Math.min(Math.max(edge, rect.left), vw - bw - edge);
    } else {
      left = Math.max(edge, rect.left - bw - gap);
      top = Math.min(Math.max(edge, rect.top), vh - bh - edge);
    }
    balloon.style.top = Math.max(edge, top) + 'px';
    balloon.style.left = Math.max(edge, left) + 'px';
  }

  function renderTutStep() {
    if (!tutActive) return;
    var step = TUT_STEPS[tutStepIndex];
    if (step.panel) setActivePanel(step.panel);
    els.tutStepCount.textContent = t('tutStepOf', { n: tutStepIndex + 1, total: TUT_STEPS.length });
    els.tutBalloonTitle.textContent = t(step.titleKey);
    els.tutBalloonText.textContent = t(step.textKey);
    els.tutBack.disabled = tutStepIndex === 0;
    els.tutNext.textContent = tutStepIndex === TUT_STEPS.length - 1 ? t('tutFinish') : t('tutNext');
    // Wait a frame so a just-triggered setActivePanel's layout change (panel
    // sliding open) has happened before measuring positions off it.
    requestAnimationFrame(function () {
      var targetEl = document.querySelector(step.target);
      if (!targetEl) { tutGoTo(tutStepIndex + 1); return; } // target not in DOM at this breakpoint — skip it
      targetEl.scrollIntoView({ block: 'nearest' });
      positionTutStep(step, targetEl);
    });
  }

  function tutGoTo(index) {
    if (index < 0) return;
    if (index >= TUT_STEPS.length) { endTutorial(); return; }
    tutStepIndex = index;
    renderTutStep();
  }

  function startTutorial() {
    tutActive = true;
    tutStepIndex = 0;
    els.tutWelcome.hidden = true;
    els.tutSpotlight.hidden = false;
    els.tutBalloon.hidden = false;
    renderTutStep();
  }

  function endTutorial() {
    tutActive = false;
    els.tutSpotlight.hidden = true;
    els.tutBalloon.hidden = true;
    try { localStorage.setItem(TUT_STORAGE_KEY, '1'); } catch (e) {}
  }

  els.tutAccept.addEventListener('click', startTutorial);
  els.tutDecline.addEventListener('click', function () {
    els.tutWelcome.hidden = true;
    try { localStorage.setItem(TUT_STORAGE_KEY, '1'); } catch (e) {}
  });
  els.tutHelpBtn.addEventListener('click', function () {
    els.tutWelcome.hidden = true;
    startTutorial();
  });
  els.tutNext.addEventListener('click', function () { tutGoTo(tutStepIndex + 1); });
  els.tutBack.addEventListener('click', function () { tutGoTo(tutStepIndex - 1); });
  els.tutSkip.addEventListener('click', endTutorial);
  window.addEventListener('resize', function () {
    if (tutActive) renderTutStep();
  });

  (function initTutorialPrompt() {
    var seen;
    try { seen = localStorage.getItem(TUT_STORAGE_KEY); } catch (e) { seen = '1'; }
    // Skipped on mobile: the guided questionnaire (see startMobileWizard)
    // already fills the "first thing a new customer sees" slot there, and
    // its full-screen overlay (z-index above this dialog) would just bury
    // this prompt underneath it anyway. The full step tutorial is still one
    // tap away afterward from the help (?) button, unchanged.
    if (!seen && !isMobileViewport()) els.tutWelcome.hidden = false;
  })();

  // ─── Init ───────────────────────────────────────────────────────────────
  renderGarmentPicker();
  renderColorPicker();
  renderSizePicker();
  renderLayersPanel();
  renderPreview();
  renderInfoPanel();
  updateWhatsappLink();
  updateGarmentGate();
  pushHistory();
  updateUndoRedoButtons();
  startMobileWizard(); // no-op on desktop — see its own comment
})();
